import { useCallback, useState, useEffect, useRef, memo, useMemo } from 'react';
import { architectureApi, type MeterLatest } from '../../api/architecture';
import { type TelemetryData } from '../../api/wattelEM';
import { CatalogDevice } from '../../api/architecture';
import { TableauElectriqueModal, tableauDisplayName, tableauBrandLabel } from './TableauElectriqueModal';
import { displayDefaultName, deviceModelName } from '../../i18n/defaultNames';
import { useDeviceModels } from '../../hooks/useDeviceModels';
import type { DeviceModel } from '../../api/deviceModels';
import { displaySn } from '../../utils/deviceDisplay';
import { useI18n } from '../../i18n';
import { Btn, Dot } from '../ui/Button';
import { IconAlign, IconRefresh, IconTrash } from '../ui/icons';
import type { TranslationKey } from '../../i18n/translations';
import ReactFlow, {
  Node,
  Edge,
  addEdge,
  Connection,
  useNodesState,
  useEdgesState,
  Controls,
  Background,
  BackgroundVariant,
  NodeProps,
  Handle,
  Position,
  MarkerType,
  useReactFlow,
  ReactFlowProvider,
} from 'reactflow';
import 'reactflow/dist/style.css';

// ============================================
// Equipment Types Configuration
// ============================================

// `name` : valeur interne (français) enregistrée dans data.type ; `labelKey` : libellé affiché.
const COMPTEUR_TYPES: { id: string; name: string; labelKey: TranslationKey; icon: string; color: string; power: string }[] = [
  { id: 'linky', name: 'Compteur Linky', labelKey: 'arch2_meter_linky', icon: '📊', color: 'yellow', power: '12 kVA' },
  { id: 'edf', name: 'Compteur EDF', labelKey: 'arch2_meter_edf', icon: '⚡', color: 'blue', power: '6 kVA' },
  { id: 'triphase', name: 'Compteur Triphasé', labelKey: 'arch2_meter_three_phase', icon: '🔋', color: 'purple', power: '36 kVA' },
  { id: 'souscompteur', name: 'Sous-compteur', labelKey: 'arch2_submeter', icon: '📈', color: 'green', power: '3 kVA' },
];

const TABLEAU_TYPES = [
  { id: '1rang-13mod', name: 'Tableau 1 rangée', icon: '🔲', modules: 13, rangees: 1, marque: 'Schneider Resi9', ref: 'R9H13401' },
  { id: '2rang-26mod', name: 'Tableau 2 rangées', icon: '🔲', modules: 26, rangees: 2, marque: 'Schneider Resi9', ref: 'R9H13402' },
  { id: '3rang-39mod', name: 'Tableau 3 rangées', icon: '🔲', modules: 39, rangees: 3, marque: 'Schneider Resi9', ref: 'R9H13403' },
  { id: '4rang-52mod', name: 'Tableau 4 rangées', icon: 'ï¿½', modules: 52, rangees: 4, marque: 'Schneider Resi9', ref: 'R9H13404' },
  { id: 'legrand-2rang', name: 'Drivia 2 rangées', icon: '🔳', modules: 26, rangees: 2, marque: 'Legrand Drivia', ref: '401212' },
  { id: 'legrand-3rang', name: 'Drivia 3 rangées', icon: '🔳', modules: 39, rangees: 3, marque: 'Legrand Drivia', ref: '401213' },
  { id: 'legrand-4rang', name: 'Drivia 4 rangées', icon: 'ï¿½', modules: 52, rangees: 4, marque: 'Legrand Drivia', ref: '401214' },
  { id: 'hager-2rang', name: 'Gamma 2 rangées', icon: '🔷', modules: 26, rangees: 2, marque: 'Hager Gamma', ref: 'GD213A' },
  { id: 'hager-3rang', name: 'Gamma 3 rangées', icon: '🔷', modules: 39, rangees: 3, marque: 'Hager Gamma', ref: 'GD313A' },
  { id: 'sous-tableau', name: 'Sous-tableau', icon: '📦', modules: 13, rangees: 1, marque: 'Modulaire', ref: 'STD-13' },
  { id: 'coffret-ext', name: 'Coffret extérieur', icon: '🏠', modules: 8, rangees: 1, marque: 'Étanche IP65', ref: 'EXT-IP65' },
];

const ZONE_TYPES: { id: string; name: string; labelKey: TranslationKey; icon: string }[] = [
  { id: 'wattel', name: 'Zone Wattel', labelKey: 'arch2_zone_wattel', icon: '⚡' },
  { id: 'eclairage', name: 'Zone Éclairage', labelKey: 'arch2_zone_lighting', icon: '💡' },
  { id: 'chauffage', name: 'Zone Chauffage', labelKey: 'arch2_zone_heating', icon: '🔥' },
  { id: 'climatisation', name: 'Zone Climatisation', labelKey: 'arch2_zone_ac', icon: '❄️' },
  { id: 'prises', name: 'Zone Prises', labelKey: 'arch2_zone_sockets', icon: '🔌' },
];

// Les modèles d'appareils (Wattel 7, Wattel Go…) viennent du catalogue zos_device_models (useDeviceModels) :
// leur code est enregistré dans le schéma, leur nom et leur image se modifient dans la console super admin.

// ============================================
// Custom Node Components
// ============================================

// Styles pour les handles de connexion - Optimisés pour tactile
const handleStyle = { 
  width: 16, 
  height: 16, 
  background: '#3b82f6',
  border: '2px solid #1d4ed8',
  borderRadius: '50%',
};

const handleStyleSource = { 
  width: 18, 
  height: 18, 
  background: '#f59e0b',
  border: '3px solid #b45309',
  borderRadius: '50%',
};

const handleStyleTarget = { 
  width: 18, 
  height: 18, 
  background: '#3b82f6',
  border: '3px solid #1d4ed8',
  borderRadius: '50%',
};

// Compteur Linky Node - Représentation ultra-réaliste optimisée pour tablette
const LinkyNode = memo(({ data, selected }: NodeProps) => {
  const { t, tp, locale } = useI18n();
  return (
    <div className={`${selected ? 'ring-4 ring-blue-500' : ''}`}>
      <Handle type="source" position={Position.Right} style={handleStyleSource} />
      
      {/* Boîtier externe du compteur Linky - Optimisé */}
      <div className="bg-gray-50 rounded-lg p-2 shadow-lg border-2 border-gray-300"
        style={{ width: '180px' }}>
        
        {/* Partie supérieure - Bandeau Enedis */}
        <div className="bg-blue-600 rounded-t px-2 py-1 mb-2">
          <div className="flex items-center justify-between">
            <div className="text-white text-[9px] font-bold tracking-wide">ENEDIS</div>
            <div className="text-white text-[7px] opacity-80">G3-PLC</div>
          </div>
        </div>

        {/* Écran LCD vert - Optimisé */}
        <div className="bg-black rounded mb-2 p-1">
          
          {/* Contenu LCD - Fond vert */}
          <div className="bg-green-500 rounded px-3 py-3">
            
            {/* Segments LCD */}
            <div className="font-mono text-black space-y-1">
              {/* Ligne 1: Logo éclair + kVA */}
              <div className="flex items-center justify-between text-[11px] font-black">
                <span className="opacity-90">⚡</span>
                <span className="tracking-wider">{data.power || '12 kVA'}</span>
              </div>
              
              {/* Ligne 2: Label INDEX */}
              <div className="text-[8px] font-bold opacity-75 tracking-wide">INDEX</div>
              
              {/* Ligne 3: Valeur index - Large et prominent */}
              <div className="text-[13px] font-black tracking-tight leading-none">
                024567
                <span className="text-[9px] ml-1 opacity-75">kWh</span>
              </div>
              
              {/* Ligne 4: Date et heure */}
              <div className="text-[7px] opacity-70 flex justify-between mt-1 pt-1 border-t border-black/20">
                <span>09/04/26</span>
                <span>{new Date().toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}</span>
              </div>
            </div>

            {/* Indicateur tarif (HC/HP) - coin supérieur droit */}
            <div className="absolute top-1 right-1 bg-black/40 px-1.5 py-0.5 rounded text-[7px] font-bold text-green-200">
              HP
            </div>
          </div>
        </div>

        {/* Zone des boutons - Layout réaliste Linky */}
        <div className="flex items-center justify-between px-1 mb-2">
          {/* Bouton + (défilement) - Simplifié */}
          <div className="relative">
            <div className="w-7 h-7 bg-gray-200 border border-gray-400 rounded-md flex items-center justify-center">
              <span className="text-gray-700 font-bold text-sm">+</span>
            </div>
            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-[6px] text-gray-500 whitespace-nowrap">DEFIL</div>
          </div>

          {/* LED de communication - Centre - Sans animation */}
          <div className="flex flex-col items-center">
            <div className="w-2.5 h-2.5 bg-green-500 rounded-full mb-0.5"></div>
            <div className="text-[6px] text-gray-600 font-medium">TIC</div>
          </div>

          {/* Bouton - (sélection) - Simplifié */}
          <div className="relative">
            <div className="w-7 h-7 bg-gray-200 border border-gray-400 rounded-md flex items-center justify-center">
              <span className="text-gray-700 font-bold text-sm">-</span>
            </div>
            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-[6px] text-gray-500 whitespace-nowrap">SELECT</div>
          </div>
        </div>

        {/* Partie basse - Informations techniques */}
        <div className="border-t border-gray-300 pt-1.5 pb-1 px-1">
          {/* Label du compteur */}
          <div className="text-center mb-1">
            <div className="font-bold text-[10px] text-gray-800">{displayDefaultName(data.label, t, tp)}</div>
            <div className="text-[7px] text-gray-500">{t('arch2_smart_meter')}</div>
          </div>

          {/* Infos techniques en bas */}
          <div className="grid grid-cols-2 gap-1 text-[6px] text-gray-600">
            <div className="bg-gray-100 rounded px-1 py-0.5 text-center">
              <div className="font-semibold text-gray-800">230V~</div>
            </div>
            <div className="bg-gray-100 rounded px-1 py-0.5 text-center">
              <div className="font-semibold text-gray-800">{data.power || '12kVA'}</div>
            </div>
          </div>

          {/* Numéro de série / PDL (si disponible) */}
          <div className="mt-1 text-center">
            <div className="text-[6px] text-gray-400 font-mono">N° {data.pdl || '12345678901234'}</div>
          </div>
        </div>

        {/* Logos certification CE en bas */}
        <div className="flex justify-center gap-1 mt-1 pb-1">
          <div className="text-[8px] bg-black text-white px-1 rounded font-bold">CE</div>
          <div className="text-[6px] text-gray-400">MID</div>
        </div>
      </div>
    </div>
  );
});

// Tableau électrique Node - Style Configurateur Rexel (vue de dessus réaliste)
const TableauNode = memo(({ data, selected }: NodeProps) => {
  const { t, tp } = useI18n();
  const wattelemList = data.wattelemList || [];
  
  // ==================== COMPOSANTS STYLE REXEL ====================
  
  // Module vide (emplacement libre)
  const ModuleVide = ({ num }: { num: number }) => (
    <div className="flex flex-col items-center">
      <div className="text-[8px] text-gray-400 mb-0.5">{num}</div>
      <div className="w-[20px] h-[70px] bg-gray-200 border border-dashed border-gray-300 rounded-sm"></div>
    </div>
  );

  // Disjoncteur style Rexel (vue de dessus avec manette T)
  const Disjoncteur = ({ num, amperage, courbe = 'C', color = 'orange', icon }: { 
    num: number; amperage: string; courbe?: string; color?: string; icon?: string 
  }) => (
    <div className="flex flex-col items-center">
      {/* Numéro module */}
      <div className="text-[8px] text-blue-600 font-bold mb-0.5">{num}</div>
      {/* Label type */}
      <div className={`w-[22px] text-center py-0.5 rounded-t-sm text-[6px] font-bold text-white
        ${color === 'orange' ? 'bg-orange-400' : color === 'cyan' ? 'bg-cyan-500' : color === 'purple' ? 'bg-purple-400' : 'bg-gray-400'}`}>
        {t('arch2_breaker_short')}
      </div>
      {/* Corps du module */}
      <div className="w-[22px] h-[50px] bg-white border border-gray-300 rounded-b-sm flex flex-col items-center justify-between py-1 relative"
        style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }}>
        {/* Manette en T (vue de dessus) */}
        <div className="relative">
          <div className="w-[14px] h-[20px] bg-gradient-to-b from-gray-500 to-gray-700 rounded-sm flex items-center justify-center"
            style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
            {/* Barre horizontale du T */}
            <div className="absolute top-1 w-[12px] h-[4px] bg-gradient-to-b from-gray-400 to-gray-600 rounded-sm"></div>
            {/* Indicateur vert ON */}
            <div className="absolute -top-0.5 right-0 w-1.5 h-1.5 bg-green-400 rounded-full shadow-sm"></div>
          </div>
        </div>
      </div>
      {/* Courbe + Ampérage */}
      <div className="text-[7px] text-gray-600 font-mono mt-0.5">{courbe}</div>
      <div className="text-[8px] text-gray-800 font-bold -mt-0.5">{amperage}</div>
      {/* Icône équipement */}
      {icon && <div className="text-blue-600 mt-1 text-[12px]">{icon}</div>}
    </div>
  );

  // Interrupteur Différentiel style Rexel (2 modules)
  const InterDiff = ({ num, amperage, sensitivity = '30mA', type = 'A' }: { 
    num: number; amperage: string; sensitivity?: string; type?: string 
  }) => (
    <div className="flex flex-col items-center">
      {/* Numéros modules */}
      <div className="flex gap-2 mb-0.5">
        <span className="text-[8px] text-blue-600 font-bold">{num}</span>
        <span className="text-[8px] text-blue-600 font-bold">{num + 1}</span>
      </div>
      {/* Label type */}
      <div className="w-[46px] text-center py-0.5 rounded-t-sm text-[6px] font-bold text-white bg-cyan-500">
        {t('arch2_rcd_short')}
      </div>
      {/* Corps du module */}
      <div className="w-[46px] h-[50px] bg-white border border-gray-300 rounded-b-sm flex flex-col items-center justify-center relative"
        style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }}>
        {/* Indicateur état */}
        <div className="absolute top-1 left-1/2 -translate-x-1/2 w-6 h-1.5 bg-green-400 rounded-sm"></div>
        {/* Manette en T (vue de dessus) - plus large */}
        <div className="w-[28px] h-[24px] bg-gradient-to-b from-gray-500 to-gray-700 rounded-sm flex items-center justify-center mt-2"
          style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
          {/* Barre horizontale du T */}
          <div className="absolute w-[24px] h-[5px] bg-gradient-to-b from-gray-400 to-gray-600 rounded-sm" style={{ marginTop: '-8px' }}></div>
        </div>
      </div>
      {/* Type + Ampérage + Sensibilité */}
      <div className="text-[7px] text-gray-600 mt-0.5">Type {type}</div>
      <div className="text-[8px] text-gray-800 font-bold">{amperage} {sensitivity}</div>
    </div>
  );

  // Contacteur Jour/Nuit style Rexel
  // const ContacteurJN = ({ num }: { num: number }) => (
  //   <div className="flex flex-col items-center">
  //     <div className="text-[8px] text-blue-600 font-bold mb-0.5">{num}</div>
  //     <div className="w-[22px] text-center py-0.5 rounded-t-sm text-[5px] font-bold text-white bg-purple-400">
  //       Ct JN
  //     </div>
  //     <div className="w-[22px] h-[50px] bg-white border border-gray-300 rounded-b-sm flex flex-col items-center justify-center"
  //       style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }}>
  //       {/* Sélecteur 3 positions */}
  //       <div className="flex flex-col items-center gap-0.5 text-[5px] text-gray-500">
  //         <span>I</span>
  //         <div className="w-2 h-2 bg-gray-600 rounded-full"></div>
  //         <span>A</span>
  //         <span>0</span>
  //       </div>
  //     </div>
  //     <div className="text-[7px] text-gray-600 mt-0.5">2F</div>
  //     <div className="text-[8px] text-gray-800 font-bold">20A</div>
  //   </div>
  // );

  // Compteur WattelEM style module
  // const ModuleWattelEM = ({ wem, startNum }: { wem: any; startNum: number }) => (
  //   <div className="flex flex-col items-center">
  //     {/* Numéros modules (prend 2 emplacements) */}
  //     <div className="flex gap-2 mb-0.5">
  //       <span className="text-[8px] text-cyan-600 font-bold">{startNum}</span>
  //       <span className="text-[8px] text-cyan-600 font-bold">{startNum + 1}</span>
  //     </div>
  //     {/* Label */}
  //     <div className={`w-[46px] text-center py-0.5 rounded-t-sm text-[5px] font-bold text-white
  //       ${wem.status === 'online' ? 'bg-cyan-500' : 'bg-gray-400'}`}>
  //       WattelEM
  //     </div>
  //     {/* Corps du module avec écran */}
  //     <div className={`w-[46px] h-[50px] border rounded-b-sm flex flex-col items-center justify-center relative
  //       ${wem.status === 'online' ? 'bg-gray-900 border-cyan-400' : 'bg-gray-700 border-gray-500'}`}
  //       style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
  //       {/* Écran LCD */}
  //       <div className={`w-10 h-6 rounded-sm flex flex-col items-center justify-center
  //         ${wem.status === 'online' ? 'bg-[#0a1f1f]' : 'bg-gray-800'}`}>
  //         <span className={`text-[6px] font-mono ${wem.status === 'online' ? 'text-cyan-400' : 'text-gray-600'}`}>
  //           230V~
  //         </span>
  //         <span className={`text-[8px] font-mono font-bold ${wem.status === 'online' ? 'text-green-400' : 'text-gray-600'}`}>
  //           {(wem.power || 0).toFixed(2)}kW
  //         </span>
  //       </div>
  //       {/* LEDs */}
  //       <div className="flex gap-1 mt-1">
  //         <div className={`w-1.5 h-1.5 rounded-full ${wem.status === 'online' ? 'bg-orange-500 animate-pulse' : 'bg-gray-600'}`}></div>
  //         <div className={`w-1.5 h-1.5 rounded-full ${wem.status === 'online' ? 'bg-green-500 animate-pulse' : 'bg-gray-600'}`}></div>
  //       </div>
  //     </div>
  //     {/* Nom */}
  //     <div className="text-[6px] text-gray-600 mt-0.5 max-w-[46px] truncate text-center" title={wem.name || wem.sn}>
  //       {(wem.name || wem.sn || '').slice(-10)}
  //     </div>
  //     {/* Icône */}
  //     <div className="text-cyan-600 mt-0.5 text-[10px]">📊</div>
  //   </div>
  // );
  
  // Données du tableau
  const rangees = data.rangees || 3;
  const totalModules = data.modules || 39;
  const modulesParRangee = Math.floor(totalModules / rangees);
  const marque = data.marque || 'Schneider Electric';

  // Générer une rangée de modules standard
  const RangeeStandard = ({ rangeeIndex, startNum }: { rangeeIndex: number; startNum: number }) => {
    const isFirstRow = rangeeIndex === 0;
    return (
      <div className="mb-3">
        <div className="flex items-end gap-0.5 bg-white p-2 rounded border border-gray-200">
          {/* Inter Diff */}
          <InterDiff 
            num={startNum} 
            amperage={isFirstRow ? "63A" : "40A"} 
            sensitivity="30mA" 
            type={isFirstRow ? "A" : "AC"} 
          />
          {/* Disjoncteurs */}
          {[...Array(Math.min(7, modulesParRangee - 5))].map((_, i) => (
            <Disjoncteur 
              key={i}
              num={startNum + 2 + i} 
              amperage={['32A', '20A', '20A', '16A', '16A', '10A', '10A'][i % 7]}
              courbe="C"
              color={rangeeIndex > 0 ? 'orange' : undefined}
              icon={['🔲', '💡', '🔌', '🔥', '🧺', '⭕', '⭕'][i % 7]}
            />
          ))}
          {/* Modules vides restants */}
          {[...Array(Math.max(0, 4))].map((_, i) => (
            <ModuleVide key={`empty-${i}`} num={startNum + 9 + i} />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className={`cursor-pointer node-hover-effect animate-fade-in-scale ${selected ? 'ring-4 ring-blue-500 ring-offset-2' : ''}`}
      style={{ minWidth: 380 }}>
      <Handle type="target" position={Position.Left} style={handleStyleTarget} />
      
      {/* ==================== COFFRET STYLE REXEL ==================== */}
      <div className="bg-white rounded-lg shadow-xl overflow-hidden border-2 border-gray-300">
        
        {/* === Header === */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white py-1.5 px-3 flex items-center justify-between">
          <span className="text-[10px] font-bold">{tableauDisplayName(data.label, t, tp)}</span>
          <span className="text-[8px] opacity-80">NF C 15-100</span>
        </div>
        
        <div className="p-3 bg-gray-50">
          
          {/* === RANGÉES DYNAMIQUES AVANT WATTELEM === */}
          {/* La zone WattelEM ne compte pas comme une rangée, donc :
              1 rangée = 1 rangée standard avant WattelEM
              2 rangées = 1 avant + 1 après
              3 rangées = 2 avant + 1 après
              4 rangées = 2 avant + 2 après
          */}
          {[...Array(Math.max(1, Math.ceil(rangees / 2)))].map((_, rowIdx) => (
            <RangeeStandard key={`row-before-${rowIdx}`} rangeeIndex={rowIdx} startNum={1 + (rowIdx * modulesParRangee)} />
          ))}
          
          {/* === ZONE WATTELEM EN VERTICAL - STYLE RÉALISTE === */}
          <div className="mb-3">
            <div className="text-[8px] text-cyan-600 font-bold mb-1 flex items-center gap-1">
              <span className="w-2 h-2 bg-cyan-500 rounded-full animate-pulse"></span>
              {t('arch2_wattelem_meters_n', { n: wattelemList.length })}
            </div>
            <div className="bg-gradient-to-b from-gray-200 to-gray-300 p-2 rounded border border-gray-400 min-h-[60px]">
              {wattelemList.length === 0 ? (
                <div className="flex items-center justify-center text-gray-500 text-[9px] py-4 bg-gray-100 rounded border border-dashed border-gray-400">
                  {t('arch2_click_add_wattelem')}
                </div>
              ) : (
                <div className="flex flex-col gap-1">
                  {/* WattelEM réaliste style rail DIN */}
                  {wattelemList.map((wem: any, index: number) => {
                    const isOnline = wem.status === 'online';
                    const voltage = Number(wem.voltage) || 0;
                    const currentA = Number(wem.current) || 0;
                    const power = Number(wem.power) || 0;
                    const energy = Number(wem.energy) || 0;
                    
                    return (
                      <div
                        key={wem.sn || index}
                        className={`flex items-stretch relative ${data.onWattelemClick ? 'cursor-pointer hover:scale-[1.02] hover:z-10 transition-transform' : ''}`}
                        onClick={(e) => {
                          if (data.onWattelemClick && wem.sn) {
                            e.stopPropagation();
                            data.onWattelemClick(wem.sn);
                          }
                        }}
                        title={data.onWattelemClick ? t('arch2_view_device', { name: wem.name || wem.sn }) : undefined}
                      >
                        {/* Module WattelEM réaliste */}
                        <div className={`flex-1 bg-gradient-to-b from-gray-100 via-white to-gray-200 rounded border-2 ${isOnline ? 'border-gray-400' : 'border-gray-300'} shadow-md overflow-hidden ${data.onWattelemClick ? 'hover:border-cyan-400 hover:ring-2 hover:ring-cyan-300/50' : ''}`}
                          style={{ minHeight: '70px' }}>
                          
                          {/* Header avec logo */}
                          <div className="bg-gradient-to-r from-gray-600 to-gray-700 px-2 py-0.5 flex items-center justify-between">
                            <span className="text-[6px] text-white font-bold tracking-wider">WATTEL EM</span>
                            <span className="text-[5px] text-gray-300">#{index + 1}</span>
                          </div>
                          
                          {/* Corps du module */}
                          <div className="p-1 flex gap-1">
                            {/* Écran LCD coloré */}
                            <div className={`flex-1 rounded border ${isOnline ? 'bg-[#001a1a] border-cyan-600' : 'bg-gray-800 border-gray-600'} p-1`}>
                              {/* Ligne 1: Tension */}
                              <div className="flex justify-between items-center">
                                <span className={`text-[9px] font-bold font-mono ${isOnline ? 'text-cyan-400' : 'text-gray-600'}`}>
                                  {voltage > 0 ? voltage.toFixed(1) : '---'}V
                                </span>
                                <span className={`text-[7px] ${isOnline ? 'text-cyan-500' : 'text-gray-600'}`}>↝</span>
                              </div>
                              {/* Ligne 2: Courant */}
                              <div className={`text-[8px] font-mono ${isOnline ? 'text-yellow-400' : 'text-gray-600'}`}>
                                {currentA > 0 ? currentA.toFixed(2) : '---'}A
                              </div>
                              {/* Ligne 3: Puissance */}
                              <div className={`text-[10px] font-bold font-mono ${isOnline ? 'text-green-400' : 'text-gray-600'}`}>
                                {power.toFixed(2)} kW
                              </div>
                              {/* Ligne 4: Énergie */}
                              <div className={`text-[8px] font-mono ${isOnline ? 'text-orange-400' : 'text-gray-600'}`}>
                                {energy > 0 ? energy.toFixed(2) : '0.00'} kWh
                              </div>
                              {/* Barre de progression */}
                              <div className="mt-0.5 h-1 bg-gray-700 rounded-full overflow-hidden">
                                <div 
                                  className={`h-full ${isOnline ? 'bg-gradient-to-r from-green-500 to-cyan-400' : 'bg-gray-600'}`}
                                  style={{ width: `${Math.min(100, power * 20)}%` }}
                                ></div>
                              </div>
                            </div>
                            
                            {/* Panneau latéral avec boutons et LEDs */}
                            <div className="flex flex-col justify-between items-center w-5">
                              {/* Bouton haut */}
                              <div className="w-3 h-3 bg-gray-400 rounded-sm border border-gray-500 shadow-inner"></div>
                              {/* LEDs */}
                              <div className="flex flex-col gap-0.5">
                                <div className={`w-2 h-2 rounded-full border ${isOnline ? 'bg-orange-500 border-orange-600 shadow-orange-500/50 shadow-sm' : 'bg-gray-500 border-gray-600'}`}></div>
                                <div className={`w-2 h-2 rounded-full border ${isOnline ? 'bg-green-500 border-green-600 shadow-green-500/50 shadow-sm animate-pulse' : 'bg-gray-500 border-gray-600'}`}></div>
                              </div>
                              {/* Bouton bas */}
                              <div className="w-3 h-3 bg-gray-400 rounded-sm border border-gray-500 shadow-inner"></div>
                            </div>
                          </div>
                          
                          {/* Footer avec nom */}
                          <div className="bg-gray-200 px-1 py-0.5 border-t border-gray-300">
                            <div className="text-[6px] text-gray-600 truncate text-center" title={wem.name || wem.sn}>
                              {(wem.name || wem.sn || '').slice(-15)}
                            </div>
                          </div>
                        </div>
                        
                        {/* Handle de sortie individuel - style connecteur */}
                        <Handle 
                          type="source" 
                          position={Position.Right} 
                          id={`wem-${wem.sn}`}
                          style={{ 
                            position: 'absolute',
                            top: '50%', 
                            right: -10, 
                            transform: 'translateY(-50%)',
                            background: isOnline ? 'linear-gradient(135deg, #06b6d4, #0891b2)' : '#6b7280',
                            width: 12,
                            height: 12,
                            border: `2px solid ${isOnline ? '#0e7490' : '#4b5563'}`,
                            borderRadius: '50%',
                            boxShadow: isOnline ? '0 0 8px rgba(6,182,212,0.6), inset 0 1px 2px rgba(255,255,255,0.3)' : 'none'
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          
          {/* === RANGÉES DYNAMIQUES APRÈS WATTELEM === */}
          {/* 1 rangée = 0 après, 2 rangées = 1 après, 3 rangées = 1 après, 4 rangées = 2 après */}
          {[...Array(Math.max(0, Math.floor(rangees / 2)))].map((_, rowIdx) => {
            const rangeesAvant = Math.ceil(rangees / 2);
            const actualRowIdx = rangeesAvant + rowIdx;
            return (
              <RangeeStandard 
                key={`row-after-${rowIdx}`} 
                rangeeIndex={actualRowIdx} 
                startNum={1 + (actualRowIdx * modulesParRangee)} 
              />
            );
          })}
          
        </div>
        
        {/* === Footer === */}
        <div className="bg-gray-200 py-1 px-3 flex items-center justify-between text-[7px] text-gray-500">
          <span>{tableauBrandLabel(marque, t)}</span>
          <span>IP30 • IK07</span>
          <span>{tp('arch2_rows', rangees)} • {tp('sch_modules_count', totalModules)}</span>
        </div>
      </div>
      
      {/* Handle de sortie général si pas de WattelEM */}
      {wattelemList.length === 0 && (
        <Handle type="source" position={Position.Right} 
          style={{
            background: '#06b6d4',
            width: 14,
            height: 14,
            border: '2px solid #0891b2',
            borderRadius: '50%',
            boxShadow: '0 2px 6px rgba(6,182,212,0.4)'
          }} />
      )}
    </div>
  );
});

// Zone Wattel Node - Simple room/area (no WattelEM inside)
const ZoneWattelNode = memo(({ data }: NodeProps) => {
  const { t, tp } = useI18n();
  return (
    <div className="border-2 rounded-lg p-3 min-w-[130px] shadow-lg cursor-pointer
      node-hover-effect animate-fade-in-scale
      bg-gradient-to-br from-purple-500 to-purple-600 border-purple-700 hover:border-purple-400
      hover:shadow-purple-500/30 hover:shadow-xl">
      <Handle type="target" position={Position.Left} style={handleStyleTarget} />
      <Handle type="source" position={Position.Right} style={handleStyleSource} />
      <div className="text-center text-white">
        <div className="text-2xl mb-1 animate-breathe">🏠</div>
        <div className="font-bold text-sm drop-shadow-md">{displayDefaultName(data.label, t, tp)}</div>
        <div className="text-xs mt-1 opacity-80">{t('arch2_zone_room')}</div>
      </div>
    </div>
  );
});

// WattelEM Node - Device in electrical panel
const WattelemNode = memo(({ data, selected }: NodeProps) => {
  const { t } = useI18n();
  const isOnline = data.status === 'online';
  return (
    <div className={`border-2 rounded-lg p-3 min-w-[130px] shadow-lg cursor-pointer
      node-hover-effect animate-fade-in-scale
      ${isOnline 
        ? 'bg-gradient-to-br from-cyan-500 to-cyan-600 border-cyan-400 animate-pulse-glow' 
        : 'bg-gradient-to-br from-gray-500 to-gray-600 border-gray-400 opacity-75'}
      ${selected ? 'ring-4 ring-cyan-300' : ''}`}>
      <Handle type="target" position={Position.Left} style={handleStyleTarget} />
      <Handle type="source" position={Position.Right} style={handleStyleSource} />
      <div className="text-center text-white">
        <div className="text-2xl mb-1">📟</div>
        <div className="font-bold text-xs">{data.label || 'WATTELEM'}</div>
        <div className="text-xs opacity-90 truncate mt-1" title={data.wattelemSn}>
          {data.wattelemSn}
        </div>
        {data.power !== undefined && data.power !== null && (
          <div className="text-xs font-semibold mt-1 bg-cyan-600/50 backdrop-blur rounded px-2 py-0.5 animate-shimmer">
            {data.power} kW
          </div>
        )}
        <div className={`text-xs mt-1 flex items-center justify-center gap-1 ${isOnline ? 'text-green-200' : 'text-red-200'}`}>
          <span className={`w-2 h-2 rounded-full ${isOnline ? 'bg-green-400 animate-pulse-led' : 'bg-red-400'}`}></span>
          {isOnline ? t('arch2_online') : t('arch2_offline')}
        </div>
      </div>
    </div>
  );
});

// Point Électrique Node
const PointElectriqueNode = memo(({ data, selected }: NodeProps) => {
  const { t, tp, language } = useI18n();
  const deviceModels = useDeviceModels();
  // Debug: log when isDeduced changes
  if (data.isDeduced) {
    console.log('[PointElectriqueNode] Rendering with isDeduced:', data.isDeduced, 'deducedEnergy:', data.deducedEnergy, 'label:', data.label);
  }
  
  const statusColors = {
    online: 'from-pink-500 to-pink-600 border-green-400',
    offline: 'from-pink-300 to-pink-400 border-red-400',
    warning: 'from-pink-400 to-pink-500 border-yellow-400',
  };
  const status = data.status || 'online';
  const deviceType = data.type || data.pointType || '';
  const deviceModel = deviceModels.find(m => m.code === deviceType);
  const deviceImage = deviceModel?.imageUrl;

  if (deviceImage) {
    return (
      <div className={`bg-white border-2 rounded-xl p-2 min-w-[100px] shadow-lg 
        node-hover-effect animate-fade-in-scale
        ${selected ? 'ring-4 ring-blue-400 animate-breathe' : 'border-gray-200'}`}>
        <Handle type="target" position={Position.Left} style={handleStyleTarget} />
        <div className="text-center">
          <img src={deviceImage} alt={deviceType} className="w-14 h-14 mx-auto object-contain" />
          <div className="font-bold text-[10px] mt-1 text-blue-600">
            {deviceModel ? deviceModelName(deviceModel, language) : deviceType}
          </div>
          <div className="font-semibold text-[9px] text-gray-600 truncate max-w-[100px]">{displayDefaultName(data.label, t, tp)}</div>
          {(data.meterPower !== undefined || data.meterEnergy !== undefined || data.deviceSn) ? (
            <div className="mt-1 bg-gray-900 rounded px-1.5 py-1 text-center min-w-[85px]">
              <div className="text-[9px] font-mono text-green-400">{data.meterVoltage > 0 ? data.meterVoltage.toFixed(0) + ' V' : (data.deviceSn ? '— V' : '')} {data.meterPower > 0 ? '\u26A1' : ''}</div>
              <div className="text-[10px] font-bold font-mono text-green-300">{((data.meterPower || 0) / 10_000_000).toFixed(3)} kW</div>
              <div className="text-[8px] font-mono text-green-500">{((data.meterEnergy || 0) / 10_000_000).toFixed(3)} kWh</div>
            </div>
          ) : data.deducedEnergy !== undefined && data.deducedEnergy > 0 ? (
            <div className="mt-1 bg-orange-900 rounded px-1.5 py-1 text-center min-w-[85px]">
              <div className="text-[8px] font-mono text-orange-300">{t('arch2_calculated')}</div>
              <div className="text-[10px] font-bold font-mono text-orange-400">{(data.deducedEnergy / 1000).toFixed(3)} kWh</div>
            </div>
          ) : data.isDeduced ? (
            <div className="mt-1 bg-orange-900 rounded px-1.5 py-1 text-center min-w-[85px]">
              <div className="text-[8px] font-mono text-orange-300">{t('arch2_calculated')}</div>
              <div className="text-[10px] font-bold font-mono text-orange-400">0.000 kWh</div>
            </div>
          ) : data.power !== undefined && data.power > 0 ? (
            <div className="text-[9px] mt-0.5 bg-gray-100 rounded px-1.5 py-0.5 font-mono text-gray-700">
              {data.power} W
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  // Fallback: Autre or unset type - pink gradient
  return (
    <div className={`bg-gradient-to-br ${statusColors[status as keyof typeof statusColors]} border-2 rounded-lg p-3 min-w-[120px] shadow-lg 
      node-hover-effect animate-fade-in-scale
      ${selected ? 'ring-4 ring-pink-300 animate-breathe' : ''}`}>
      <Handle type="target" position={Position.Left} style={handleStyleTarget} />
      <div className="text-center text-white">
        <div className="text-2xl mb-1">{data.icon || '⚙️'}</div>
        <div className="font-bold text-xs">{displayDefaultName(data.label, t, tp)}</div>
        {(data.meterPower !== undefined || data.meterEnergy !== undefined || data.deviceSn) ? (
          <div className="mt-1 bg-black/40 backdrop-blur rounded px-2 py-1 text-center">
            <div className="text-[9px] font-mono text-green-400">{data.meterVoltage > 0 ? data.meterVoltage.toFixed(0) + ' V' : (data.deviceSn ? '— V' : '')} {data.meterPower > 0 ? '\u26A1' : ''}</div>
            <div className="text-[10px] font-bold font-mono text-green-300">{((data.meterPower || 0) / 10_000_000).toFixed(3)} kW</div>
            <div className="text-[8px] font-mono text-green-400">{((data.meterEnergy || 0) / 10_000_000).toFixed(3)} kWh</div>
          </div>
        ) : data.deducedEnergy !== undefined && data.deducedEnergy > 0 ? (
          <div className="mt-1 bg-orange-900/80 backdrop-blur rounded px-2 py-1 text-center">
            <div className="text-[8px] font-mono text-orange-300">{t('arch2_calculated')}</div>
            <div className="text-[10px] font-bold font-mono text-orange-400">{(data.deducedEnergy / 1000).toFixed(3)} kWh</div>
          </div>
        ) : data.isDeduced ? (
          <div className="mt-1 bg-orange-900/80 backdrop-blur rounded px-2 py-1 text-center">
            <div className="text-[8px] font-mono text-orange-300">{t('arch2_calculated')}</div>
            <div className="text-[10px] font-bold font-mono text-orange-400">0.000 kWh</div>
          </div>
        ) : data.power !== undefined && data.power > 0 ? (
          <div className="text-xs mt-1 bg-pink-600/50 backdrop-blur rounded px-2 py-0.5 font-mono">
            {data.power} W
          </div>
        ) : null}
      </div>
    </div>
  );
});

const EquipmentNode = memo(({ data, selected }: NodeProps) => {
  const icons: Record<string, string> = {
    prise: '­ƒöî',
    lumiere: '­ƒÆí',
    chauffage: '­ƒöÑ',
    climatisation: 'ÔØä´©Å',
    default: 'ÔÜí',
  };
  
  return (
    <div className={`bg-gradient-to-br from-white to-gray-50 border-2 border-gray-300 rounded-lg p-2 min-w-[80px] shadow-lg
      node-hover-effect animate-fade-in-scale hover:border-blue-400 hover:shadow-blue-200/50
      ${selected ? 'ring-4 ring-blue-400' : ''}`}>
      <Handle type="target" position={Position.Left} style={handleStyle} />
      <div className="text-center">
        <div className="text-xl">{icons[data.type as string] || icons.default}</div>
        <div className="text-xs font-medium text-gray-700">{data.label}</div>
      </div>
    </div>
  );
});

// Compteur EDF classique (ancien mod├¿le ├®lectrom├®canique bleu) - Optimis├®
const CompteurEDFNode = memo(({ data, selected }: NodeProps) => {
  const { t, tp } = useI18n();
  return (
    <div className={`${selected ? 'ring-4 ring-blue-500' : ''}`}>
      <Handle type="source" position={Position.Right} style={handleStyleSource} />
      
      {/* Bo├«tier bleu EDF classique - Optimis├® */}
      <div className="bg-blue-600 rounded-lg p-2 shadow-lg border-2 border-blue-800"
        style={{ width: '160px' }}>
        
        {/* Logo EDF - Simplifi├® */}
        <div className="bg-white rounded px-2 py-1 mb-2 text-center">
          <div className="text-blue-600 text-sm font-black tracking-tight">EDF</div>
        </div>

        {/* Cadran rotatif ├®lectrom├®canique - Optimis├® */}
        <div className="bg-white rounded-lg mb-2 p-2">
          
          {/* Disque rotatif - Simplifi├® */}
          <div className="relative h-16 flex items-center justify-center mb-2">
            <div className="absolute w-14 h-14 rounded-full bg-gray-200 border-2 border-gray-400">
              {/* Marque rouge sur le disque */}
              <div className="absolute top-2 left-1/2 w-1 h-3 bg-red-600 -translate-x-1/2 rounded-full"></div>
              {/* Anneaux concentriques */}
              <div className="absolute inset-3 rounded-full border border-gray-400 opacity-30"></div>
              <div className="absolute inset-5 rounded-full border border-gray-400 opacity-20"></div>
            </div>
          </div>

          {/* Rouleaux de comptage */}
          <div className="bg-black rounded px-2 py-1 flex justify-center gap-0.5">
            {['0','2','4','5','6','7'].map((digit, i) => (
              <div key={i} className="bg-white text-black font-bold text-sm w-4 h-5 flex items-center justify-center rounded-sm border border-gray-300">
                {digit}
              </div>
            ))}
            <span className="text-white text-[10px] ml-1 self-end">kWh</span>
          </div>
        </div>

        {/* Plaque signal├®tique */}
        <div className="bg-gray-200 rounded p-1.5 mb-1 text-[7px] text-gray-800">
          <div className="flex justify-between mb-0.5">
            <span className="font-semibold">{displayDefaultName(data.label, t, tp)}</span>
            <span>{data.power || '6 kVA'}</span>
          </div>
          <div className="flex justify-between text-[6px]">
            <span>230V~ 50Hz</span>
            <span>{t('arch2_single_phase_short')}</span>
          </div>
        </div>
      </div>
    </div>
  );
});

// Compteur Triphasé (industriel/commercial) - Optimisé
const CompteurTriphaseNode = memo(({ data, selected }: NodeProps) => {
  const { t, tp } = useI18n();
  return (
    <div className={`${selected ? 'ring-4 ring-blue-500' : ''}`}>
      <Handle type="source" position={Position.Right} style={handleStyleSource} />
      
      {/* Boîtier gris métallique industriel - Optimisé */}
      <div className="bg-gray-700 rounded-lg p-2 shadow-lg border-2 border-gray-600"
        style={{ width: '180px' }}>
        
        {/* Bandeau de warning - Simplifié */}
        <div className="bg-yellow-400 border-2 border-yellow-600 rounded px-2 py-1 mb-2 flex items-center justify-center gap-1">
          <span className="text-lg">⚠️</span>
          <span className="text-black text-[8px] font-black">{t('arch2_three_phase_400')}</span>
        </div>

        {/* Écran LCD numérique blanc/bleu - Optimisé */}
        <div className="bg-blue-900 rounded-lg mb-2 p-2 border border-blue-700">
          
          {/* Affichage LCD bleu - Simplifié */}
          <div className="bg-blue-300 rounded p-2">
            <div className="font-mono text-blue-950 space-y-0.5">
              {/* Phases L1 L2 L3 */}
              <div className="text-[7px] font-bold border-b border-blue-500/30 pb-0.5">
                <div className="flex justify-between">
                  <span>L1: 230V</span>
                  <span>12.5A</span>
                </div>
                <div className="flex justify-between">
                  <span>L2: 231V</span>
                  <span>11.8A</span>
                </div>
                <div className="flex justify-between">
                  <span>L3: 229V</span>
                  <span>13.2A</span>
                </div>
              </div>
              
              {/* Puissance totale */}
              <div className="text-center text-[11px] font-black">
                {data.power || '36 kVA'}
              </div>
              
              {/* Index */}
              <div className="text-[8px] text-center">
                <div className="opacity-75">{t('arch2_index_total')}</div>
                <div className="text-[10px] font-bold">156789 kWh</div>
              </div>
            </div>
          </div>
        </div>

        {/* LEDs indicateurs de phases - Sans animation */}
        <div className="flex justify-around mb-2 bg-black/30 rounded p-1">
          <div className="flex flex-col items-center">
            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
            <span className="text-[6px] text-gray-300">L1</span>
          </div>
          <div className="flex flex-col items-center">
            <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
            <span className="text-[6px] text-gray-300">L2</span>
          </div>
          <div className="flex flex-col items-center">
            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            <span className="text-[6px] text-gray-300">L3</span>
          </div>
        </div>

        {/* Plaque signalétique */}
        <div className="bg-gray-300 rounded p-1.5 text-[7px] text-gray-900">
          <div className="font-bold text-center mb-0.5">{displayDefaultName(data.label, t, tp)}</div>
          <div className="grid grid-cols-2 gap-x-2 text-[6px]">
            <div>400V 3Ph+N</div>
            <div className="text-right font-bold">{data.power || '36kVA'}</div>
          </div>
        </div>
      </div>
    </div>
  );
});

// Sous-compteur (moderne, compact) - Optimisé
const SousCompteurNode = memo(({ data, selected }: NodeProps) => {
  const { t, tp } = useI18n();
  return (
    <div className={`${selected ? 'ring-4 ring-blue-500' : ''}`}>
      <Handle type="source" position={Position.Right} style={handleStyleSource} />
      
      {/* Boîtier compact vert/blanc - Optimisé */}
      <div className="bg-green-50 rounded-lg p-2 shadow-lg border-2 border-green-400"
        style={{ width: '140px' }}>
        
        {/* Bandeau titre - Simplifié */}
        <div className="bg-green-500 rounded px-2 py-1 mb-2">
          <div className="text-white text-[8px] font-bold text-center">{t('arch2_submeter_caps')}</div>
        </div>

        {/* Écran LCD petit format - Optimisé */}
        <div className="bg-black rounded mb-2 p-1">
          
          <div className="bg-cyan-300 rounded px-2 py-1.5">
            <div className="font-mono text-cyan-950 text-center">
              {/* Puissance instantanée */}
              <div className="text-[9px] font-black mb-0.5">
                2.4 kW
              </div>
              {/* Index */}
              <div className="text-[7px] opacity-75 mb-0.5">INDEX</div>
              <div className="text-[10px] font-bold">
                04567
                <span className="text-[7px] ml-0.5">kWh</span>
              </div>
            </div>
          </div>
        </div>

        {/* LED et reset - Sans animation */}
        <div className="flex items-center justify-between mb-2 px-1">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span className="text-[6px] text-gray-600">{t('arch2_active_caps')}</span>
          </div>
          <button className="bg-gray-300 border border-gray-400 rounded px-1.5 py-0.5 text-[6px] font-semibold">
            RST
          </button>
        </div>

        {/* Info */}
        <div className="bg-white rounded p-1 border border-green-300">
          <div className="text-center text-[8px] font-bold text-gray-800">{displayDefaultName(data.label, t, tp)}</div>
          <div className="text-center text-[6px] text-gray-600">{data.power || '3 kVA'} • 230V~</div>
        </div>
      </div>
    </div>
  );
});

// Node types registry - exported for use in ConsultationSchemaView
export const nodeTypes = {
  linky: LinkyNode,
  edf: CompteurEDFNode,
  triphase: CompteurTriphaseNode,
  souscompteur: SousCompteurNode,
  tableau: TableauNode,
  zone: ZoneWattelNode,
  wattelem: WattelemNode,
  point: PointElectriqueNode,
  equipment: EquipmentNode,
};

// ============================================
// Initial Data (matching your schema)
// ============================================

const initialNodes: Node[] = [
  // Compteur Linky
  {
    id: 'linky',
    type: 'linky',
    position: { x: 50, y: 250 },
    data: { label: 'Linky', type: 'Compteur', power: '12 kVA', icon: '📊', color: 'yellow' },
  },
  // Tableau électrique
  {
    id: 'tableau',
    type: 'tableau',
    position: { x: 250, y: 250 },
    data: { label: 'Tableau Électrique', modules: 24 },
  },
];

const initialEdges: Edge[] = [
  // Linky -> Tableau
  {
    id: 'e-linky-tableau',
    source: 'linky',
    target: 'tableau',
    type: 'smoothstep',
    style: { stroke: '#666', strokeWidth: 3 },
    markerEnd: { type: MarkerType.ArrowClosed },
  },
];

// ============================================
// Main Component
// ============================================

interface DeviceInfo {
  deviceSn: string;
  online: boolean;
  lastSeen: string | null;
  powerKw: number | null;
  voltageV: number | null;
  currentA: number | null;
  relayState: number | null;
}

// Borne de recharge OCPP (WattelGo/WattelWall, Wattel 22, etc.) provenant du
// device-service (OCPP) + org_bornes. Sélectionnable pour un Point dans le schéma.
export interface OcppBorne {
  deviceId: number;       // ID numérique device-service → utilisé pour la navigation ?deviceId= (mode catalogue réel)
  deviceSn: string;       // Numéro de série OCPP
  deviceName: string;     // Nom affiché (borne_name de l'org ou nom device)
  model?: string;         // 'WattelGo' | 'WattelWall' | 'Borne'
  online?: boolean;
}

interface ElectricalSchemaFlowProps {
  onNodeSelect?: (node: Node | null) => void;
  editable?: boolean;
  onAddZone?: () => void;
  onAddPoint?: () => void;
  onAddCompteur?: () => void;
  onNodesEdgesChange?: (nodes: Node[], edges: Edge[]) => void;
  initialNodesOverride?: Node[];
  initialEdgesOverride?: Edge[];
  devices?: DeviceInfo[];
  catalogDevices?: CatalogDevice[];
  ocppBornes?: OcppBorne[];
  meterData?: MeterLatest[];
  wattelemTelemetry?: Record<string, TelemetryData>;
  isVerticalLayout?: boolean; // Mode portrait - disposition verticale
  onTableauClick?: (tableauNode: Node) => void; // Callback pour clic sur tableau en mode consultation
  onPointClick?: (pointNode: Node) => void; // Callback pour clic sur point électrique en mode consultation
  onWattelemClick?: (sn: string) => void; // Callback pour clic sur un WattelEM précis dans un tableau
}

// Internal component that uses ReactFlow hooks
function ElectricalSchemaFlowInternal({ 
  onNodeSelect, 
  editable = true, 
  onNodesEdgesChange,
  initialNodesOverride,
  initialEdgesOverride,
  devices = [],
  catalogDevices = [],
  ocppBornes = [],
  meterData = [],
  wattelemTelemetry = {},
  isVerticalLayout = false,
  onTableauClick,
  onPointClick,
  onWattelemClick,
}: ElectricalSchemaFlowProps) {
  const reactFlowInstance = useReactFlow();
  const { t, tp, language } = useI18n();
  const deviceModels = useDeviceModels();

  // Use override nodes if provided and not empty, otherwise use defaults
  const startNodes = (initialNodesOverride && initialNodesOverride.length > 0) ? initialNodesOverride : initialNodes;
  const startEdges = (initialEdgesOverride && initialEdgesOverride.length > 0) ? initialEdgesOverride : initialEdges;
  
  const [nodes, setNodes, onNodesChange] = useNodesState(startNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(startEdges);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [selectedEdge, setSelectedEdge] = useState<Edge | null>(null);
  const [nodeCount, setNodeCount] = useState({ zone: 0, point: 0, compteur: 1, tableau: 1 });
  
  // Modal states
  const [showCompteurModal, setShowCompteurModal] = useState(false);
  const [showTableauModal, setShowTableauModal] = useState(false);
  const [showZoneModal, setShowZoneModal] = useState(false);
  const [showPointModal, setShowPointModal] = useState(false);
  // Device assignment modal
  const [editingZoneNode, setEditingZoneNode] = useState<Node | null>(null);
  const [editingPointNode, setEditingPointNode] = useState<Node | null>(null);
  const [editingTableauNode, setEditingTableauNode] = useState<Node | null>(null);
  // Saisie par numéro de série pour le sélecteur d'appareil d'un point/borne
  const [pointSnInput, setPointSnInput] = useState('');
  const [pointSnError, setPointSnError] = useState<string | null>(null);
  const [pointSnChecking, setPointSnChecking] = useState(false);
  const [zoneSnError, setZoneSnError] = useState<string | null>(null);

  // Center view on mount (when entering edit mode)
  useEffect(() => {
    if (editable && nodes.length > 0) {
      setTimeout(() => {
        reactFlowInstance.fitView({ padding: 0.2, duration: 800 });
      }, 100);
    }
  }, [editable, reactFlowInstance]);

  // Update nodes/edges when overrides change
  // Preserve telemetry properties (meterPower, meterEnergy, deducedEnergy, isDeduced) when updating
  useEffect(() => {
    if (initialNodesOverride && initialNodesOverride.length > 0) {
      setNodes(currentNodes => {
        // If the incoming nodes are completely different (different IDs), just replace
        const currentIds = new Set(currentNodes.map(n => n.id));
        const newIds = new Set(initialNodesOverride.map(n => n.id));
        const sameStructure = initialNodesOverride.every(n => currentIds.has(n.id)) && 
                              currentNodes.every(n => newIds.has(n.id));
        
        if (!sameStructure) {
          // Complete replacement - new installation loaded
          return initialNodesOverride;
        }
        
        // Same structure - preserve telemetry data
        const telemetryMap = new Map(
          currentNodes.map(n => [n.id, {
            meterPower: n.data?.meterPower,
            meterEnergy: n.data?.meterEnergy,
            meterVoltage: n.data?.meterVoltage,
            deducedEnergy: n.data?.deducedEnergy,
            isDeduced: n.data?.isDeduced,
          }])
        );
        
        // Merge new nodes with preserved telemetry
        return initialNodesOverride.map(n => {
          const preserved = telemetryMap.get(n.id);
          if (preserved && (preserved.meterPower !== undefined || preserved.isDeduced)) {
            return {
              ...n,
              data: {
                ...n.data,
                ...preserved,
              }
            };
          }
          return n;
        });
      });
    }
  }, [initialNodesOverride, setNodes]);

  useEffect(() => {
    if (initialEdgesOverride && initialEdgesOverride.length > 0) {
      setEdges(initialEdgesOverride);
    }
  }, [initialEdgesOverride, setEdges]);

  // Refs to track current state without causing re-renders
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;
  const edgesRef = useRef(edges);
  edgesRef.current = edges;

  // ====== AUTO-LAYOUT FUNCTION ======
  // Aligne automatiquement les nœuds - horizontal ou vertical selon isVerticalLayout
  const autoLayoutNodes = useCallback(() => {
    setNodes(currentNodes => {
      // Create completely new nodes array with recalculated positions
      const nodesCopy = currentNodes.map(n => ({ 
        ...n, 
        data: { ...n.data },
      }));
      
      // Build parent-child relationships
      const parentMap = new Map<string, string>();
      edgesRef.current.forEach(e => parentMap.set(e.target, e.source));
      
      // Find nodes by type
      const linkyNodes = nodesCopy.filter(n => n.type === 'linky' || n.type === 'edf' || n.type === 'triphase' || n.type === 'souscompteur');
      const tableauNodes = nodesCopy.filter(n => n.type === 'tableau');
      const zoneNodes = nodesCopy.filter(n => n.type === 'zone');
      const pointNodes = nodesCopy.filter(n => n.type === 'point');
      
      // Group points by parent
      const pointsByParent = new Map<string, typeof pointNodes>();
      pointNodes.forEach(point => {
        const parentId = parentMap.get(point.id);
        if (parentId) {
          if (!pointsByParent.has(parentId)) {
            pointsByParent.set(parentId, []);
          }
          pointsByParent.get(parentId)!.push(point);
        }
      });

      if (isVerticalLayout) {
        // ===== MODE VERTICAL (Portrait) =====
        // Disposition: Linky en haut → Tableau → Zones → Points en bas
        const startY = 50;
        const centerX = 800;
        const minNodeGap = 220; // Espacement horizontal entre nodes
        
        // Row 0: Linky/Compteurs (hauteur ~200px)
        const linkyTotalWidth = (linkyNodes.length - 1) * minNodeGap;
        linkyNodes.forEach((linky, index) => {
          linky.position = { 
            x: centerX - linkyTotalWidth / 2 + index * minNodeGap, 
            y: startY 
          };
        });
        
        // Row 1: Tableaux (hauteur ~350px pour un tableau) - position après Linky
        const tableauY = startY + 280; // Après le Linky
        const tableauTotalWidth = (tableauNodes.length - 1) * 400;
        tableauNodes.forEach((tableau, index) => {
          tableau.position = { 
            x: centerX - tableauTotalWidth / 2 + index * 400 - 150, // Centrer le tableau (largeur ~300px)
            y: tableauY 
          };
        });
        
        // Row 2: Zones - position après les tableaux (tableau fait ~400px de haut)
        const zonesY = tableauY + 450;
        const zoneGap = 180;
        const zoneTotalWidth = (zoneNodes.length - 1) * zoneGap;
        zoneNodes.forEach((zone, index) => {
          zone.position = { 
            x: centerX - zoneTotalWidth / 2 + index * zoneGap - 60, // Centrer la zone
            y: zonesY 
          };
        });
        
        // Row 3: Points - alignés sous leur zone parente
        const pointsY = zonesY + 200;
        pointsByParent.forEach((points, parentId) => {
          const parentNode = nodesCopy.find(n => n.id === parentId);
          if (parentNode && points.length > 0) {
            const parentCenterX = parentNode.position.x + 70; // Centre de la zone
            const pointGap = 160;
            const totalWidth = (points.length - 1) * pointGap;
            const startPointX = parentCenterX - totalWidth / 2;
            
            points.forEach((point, idx) => {
              point.position = {
                x: startPointX + idx * pointGap,
                y: pointsY
              };
            });
          }
        });
        
        console.log('[AutoLayout] VERTICAL layout applied');
      } else {
        // ===== MODE HORIZONTAL (Paysage) - défaut =====
        const startX = 80;
        const baseY = 300;
        const colGap = 450;
        const minPointGap = 250;
        
        // Column 0: Linky/Compteurs
        linkyNodes.forEach((linky, index) => {
          linky.position = { x: startX, y: baseY + index * 250 };
        });
        
        // Column 1: Tableaux
        const tableauY = linkyNodes.length > 0 ? linkyNodes[0].position.y - 50 : baseY;
        tableauNodes.forEach((tableau, index) => {
          tableau.position = { x: startX + colGap, y: tableauY + index * 350 };
        });
        
        // Calculate required height for each zone
        const zoneHeights = new Map<string, number>();
        zoneNodes.forEach(zone => {
          const children = pointsByParent.get(zone.id) || [];
          const requiredHeight = children.length > 0 ? children.length * minPointGap : 100;
          zoneHeights.set(zone.id, requiredHeight);
        });
        
        // Position zones
        const tableauCenterY = tableauY + 175;
        let currentY = tableauCenterY;
        
        if (zoneNodes.length === 1) {
          zoneNodes[0].position = { x: startX + colGap * 2, y: tableauCenterY - 40 };
        } else if (zoneNodes.length > 0) {
          const totalHeight = Array.from(zoneHeights.values()).reduce((sum, h) => sum + h, 0);
          currentY = tableauCenterY - totalHeight / 2;
          
          zoneNodes.forEach((zone) => {
            const zoneHeight = zoneHeights.get(zone.id) || 100;
            zone.position = { x: startX + colGap * 2, y: currentY };
            currentY += zoneHeight;
          });
        }
        
        // Column 3: Points
        pointsByParent.forEach((points, parentId) => {
          const parentNode = nodesCopy.find(n => n.id === parentId);
          if (parentNode && points.length > 0) {
            const parentCenterY = parentNode.position.y + 40;
            const totalHeight = (points.length - 1) * minPointGap;
            const startPointY = parentCenterY - totalHeight / 2;
            
            points.forEach((point, idx) => {
              point.position = {
                x: startX + colGap * 3,
                y: startPointY + idx * minPointGap
              };
            });
          }
        });
        
        console.log('[AutoLayout] HORIZONTAL layout applied');
      }
      
      return nodesCopy; // Return completely new array
    });
    
    // Notify parent of change
    setTimeout(() => {
      if (onNodesEdgesChange) {
        onNodesEdgesChange(nodesRef.current, edgesRef.current);
      }
    }, 100);
  }, [setNodes, onNodesEdgesChange, isVerticalLayout]);

  // Track if we've applied initial layout
  const hasAppliedInitialLayout = useRef(false);
  const lastVerticalLayout = useRef(isVerticalLayout);

  // Apply layout only on mount or when isVerticalLayout actually changes
  useEffect(() => {
    // Only apply if vertical mode changed or we haven't done initial layout
    if (nodes.length > 0 && (lastVerticalLayout.current !== isVerticalLayout || !hasAppliedInitialLayout.current)) {
      console.log('[ElectricalSchemaFlow] Applying layout, isVertical:', isVerticalLayout);
      lastVerticalLayout.current = isVerticalLayout;
      hasAppliedInitialLayout.current = true;
      
      // Small delay to ensure nodes are loaded
      const timer = setTimeout(() => {
        autoLayoutNodes();
        setTimeout(() => {
          reactFlowInstance.fitView({ padding: 0.15, duration: 500 });
        }, 300);
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [isVerticalLayout, nodes.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Garde-fou « schéma vide » : après une longue veille (onglet en arrière-plan, écran de tablette
  // éteint, rotation), le viewport ReactFlow peut devenir invalide ou le schéma sortir entièrement
  // de l'écran, et rien ne le recentre : l'utilisateur devait recharger la page. On vérifie au
  // retour de visibilité, au redimensionnement et toutes les 15 s ; si aucun nœud n'est visible
  // (et que l'utilisateur ne manipule pas le schéma), on recentre. Sinon on ne touche à rien.
  const flowWrapperRef = useRef<HTMLDivElement>(null);
  const overrideRef = useRef(initialNodesOverride);
  overrideRef.current = initialNodesOverride;
  useEffect(() => {
    const el = flowWrapperRef.current;
    let lastInteraction = 0;
    const touch = () => { lastInteraction = Date.now(); };
    const recover = (reason: string) => {
      console.warn(`[ElectricalSchemaFlow] Schéma invisible (${reason}) → recentrage automatique`);
      try { reactFlowInstance.setViewport({ x: 0, y: 0, zoom: 1 }); } catch { /* instance indisponible */ }
      setTimeout(() => { try { reactFlowInstance.fitView({ padding: 0.15, duration: 300 }); } catch { /* idem */ } }, 60);
    };
    const check = () => {
      if (document.hidden || !el) return;
      if (Date.now() - lastInteraction < 8000) return;
      const { width, height } = el.getBoundingClientRect();
      if (width < 50 || height < 50) return;
      const list = reactFlowInstance.getNodes();
      if (list.length === 0) {
        const src = overrideRef.current;
        if (src && src.length > 0) { console.warn('[ElectricalSchemaFlow] Nœuds perdus → restauration'); setNodes(src); setTimeout(() => recover('nœuds restaurés'), 200); }
        return;
      }
      const vp = reactFlowInstance.getViewport();
      if (![vp.x, vp.y, vp.zoom].every(Number.isFinite) || vp.zoom <= 0) { recover('viewport invalide'); return; }
      const visible = list.some(n => {
        const w = (n.width ?? 160) * vp.zoom, h = (n.height ?? 80) * vp.zoom;
        const x = n.position.x * vp.zoom + vp.x, y = n.position.y * vp.zoom + vp.y;
        return x + w > 0 && y + h > 0 && x < width && y < height;
      });
      if (!visible) recover('aucun nœud à l’écran');
    };
    const onVisible = () => { if (!document.hidden) setTimeout(check, 400); };
    const onResize = () => setTimeout(check, 400);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    el?.addEventListener('pointerdown', touch, { passive: true });
    el?.addEventListener('wheel', touch, { passive: true });
    el?.addEventListener('touchstart', touch, { passive: true });
    const id = setInterval(check, 15000);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      el?.removeEventListener('pointerdown', touch);
      el?.removeEventListener('wheel', touch);
      el?.removeEventListener('touchstart', touch);
    };
  }, [reactFlowInstance, setNodes]);

  // Notify parent on drag end only (for position updates)
  const onNodeDragStop = useCallback(
    () => {
      if (onNodesEdgesChange) {
        onNodesEdgesChange(nodesRef.current, edgesRef.current);
      }
    },
    [onNodesEdgesChange]
  );

  // Notify parent when nodes/edges are added or removed
  const notifyParent = useCallback(() => {
    if (onNodesEdgesChange) {
      // Use setTimeout to ensure state is updated
      setTimeout(() => {
        onNodesEdgesChange(nodesRef.current, edgesRef.current);
      }, 0);
    }
  }, [onNodesEdgesChange]);

  // Update a node's data in-place (must be before onConnect which depends on it)
  const updateNodeData = useCallback((nodeId: string, updates: Record<string, any>) => {
    setNodes((nds) => {
      const updated = nds.map(n => {
        if (n.id !== nodeId) return n;
        return { ...n, data: { ...n.data, ...updates } };
      });
      // Notify parent immediately with the updated nodes (not from stale ref)
      if (onNodesEdgesChange) {
        setTimeout(() => onNodesEdgesChange(updated, edgesRef.current), 0);
      }
      return updated;
    });
  }, [setNodes, onNodesEdgesChange]);

  // Delete a node and its edges
  const deleteNode = useCallback((nodeId: string) => {
    setNodes((nds) => nds.filter(n => n.id !== nodeId));
    setEdges((eds) => eds.filter(e => e.source !== nodeId && e.target !== nodeId));
    setEditingZoneNode(null);
    setEditingPointNode(null);
    setEditingTableauNode(null);
    notifyParent();
  }, [setNodes, setEdges, notifyParent]);

  // Add WattelEM to Tableau (inside the tableau node, not as separate node)
  const addWattelemToPanel = useCallback((wattelemDevice: any, tableauId: string) => {
    // Update the tableau node to include this WattelEM in its wattelemList
    setNodes((nds) => {
      const updatedNodes = nds.map(n => {
        if (n.id !== tableauId) return n;
        
        const currentList = n.data?.wattelemList || [];
        
        // Check if already exists
        if (currentList.some((w: any) => w.sn === wattelemDevice.sn)) {
          return n;
        }
        
        // Add to list
        const newList = [...currentList, {
          sn: wattelemDevice.sn,
          name: wattelemDevice.name || wattelemDevice.sn,
          power: wattelemDevice.power || 0,
          status: wattelemDevice.status || 'offline',
        }];
        
        return {
          ...n,
          data: { ...n.data, wattelemList: newList }
        };
      });
      
      // Update editingTableauNode to refresh the modal
      const updatedTableau = updatedNodes.find(n => n.id === tableauId);
      if (updatedTableau) {
        setEditingTableauNode(updatedTableau);
      }
      
      return updatedNodes;
    });
    
    notifyParent();
  }, [setNodes, notifyParent]);

  // Remove WattelEM from Tableau
  const removeWattelemFromPanel = useCallback((deviceSn: string, tableauId: string) => {
    setNodes((nds) => {
      const updatedNodes = nds.map(n => {
        if (n.id !== tableauId) return n;
        
        const currentList = n.data?.wattelemList || [];
        const newList = currentList.filter((w: any) => w.sn !== deviceSn);
        
        return {
          ...n,
          data: { ...n.data, wattelemList: newList }
        };
      });
      
      // Update editingTableauNode to refresh the modal
      const updatedTableau = updatedNodes.find(n => n.id === tableauId);
      if (updatedTableau) {
        setEditingTableauNode(updatedTableau);
      }
      
      return updatedNodes;
    });
    
    // Also remove any edges connected to this WattelEM
    setEdges((eds) => eds.filter(e => e.sourceHandle !== `wem-${deviceSn}`));
    
    notifyParent();
  }, [setNodes, setEdges, notifyParent]);

  // Add new Zone
  const addZone = useCallback((zoneType: typeof ZONE_TYPES[0]) => {
    const newZoneId = `zone${nodeCount.zone + 1}`;
    const newZone: Node = {
      id: newZoneId,
      type: 'zone',
      position: { x: 480, y: 100 + (nodeCount.zone * 170) },
      data: { 
        label: `${t(zoneType.labelKey)} ${nodeCount.zone + 1}`,
        icon: zoneType.icon,
        zoneType: zoneType.id,
        power: 0, 
        status: 'online' 
      },
    };
    
    // Ne pas créer de liaison automatique - l'utilisateur doit la faire manuellement
    setNodes((nds) => [...nds, newZone]);
    setNodeCount((prev) => ({ ...prev, zone: prev.zone + 1 }));
    setShowZoneModal(false);
    notifyParent();
  }, [nodeCount.zone, setNodes, notifyParent, t]);

  // Add new Point Électrique (modèle du catalogue ; le nom enregistré est le nom principal du modèle,
  // affiché ensuite dans la langue de l'interface et suivi en cas de renommage)
  const addPoint = useCallback((pointType: DeviceModel) => {
    const newPointId = `pe-new-${nodeCount.point + 1}`;
    const newPoint: Node = {
      id: newPointId,
      type: 'point',
      position: { x: 700, y: 50 + (nodeCount.point * 80) },
      data: { 
        label: pointType.name,
        icon: pointType.icon || '⚙️',
        pointType: pointType.code,
        power: 0, 
        status: 'online' 
      },
    };

    setNodes((nds) => [...nds, newPoint]);
    setNodeCount((prev) => ({ ...prev, point: prev.point + 1 }));
    setShowPointModal(false);
    notifyParent();
  }, [nodeCount.point, setNodes, notifyParent, t]);

  // Add new Compteur
  const addCompteur = useCallback((compteurType: typeof COMPTEUR_TYPES[0]) => {
    const newCompteurId = `compteur-${nodeCount.compteur + 1}`;
    const newCompteur: Node = {
      id: newCompteurId,
      type: compteurType.id, // Utilise le type spécifique (linky, edf, triphase, souscompteur)
      position: { x: 50, y: 100 + (nodeCount.compteur * 200) },
      data: { 
        label: t(compteurType.labelKey),
        type: compteurType.name, // valeur interne (français), ne pas traduire
        icon: compteurType.icon,
        color: compteurType.color,
        power: compteurType.power 
      },
    };

    // Ne pas créer de liaison automatique - l'utilisateur doit la faire manuellement
    setNodes((nds) => [...nds, newCompteur]);
    setNodeCount((prev) => ({ ...prev, compteur: prev.compteur + 1 }));
    setShowCompteurModal(false);
    notifyParent();
  }, [nodeCount.compteur, setNodes, notifyParent, t]);

  // Add new Tableau
  const addTableau = useCallback((tableauType: typeof TABLEAU_TYPES[0]) => {
    const newTableauId = `tableau-${nodeCount.tableau + 1}`;
    const newTableau: Node = {
      id: newTableauId,
      type: 'tableau',
      position: { x: 250, y: 100 + (nodeCount.tableau * 200) },
      data: {
        // Nom par défaut en français : enregistré comme nom de zone et reconnu par le préfixe
        // « Tableau » au rechargement. Seul l'affichage est traduit (tableauDisplayName).
        label: tableauType.name,
        icon: tableauType.icon,
        modules: tableauType.modules,
        rangees: tableauType.rangees,
        marque: tableauType.marque
      },
    };

    setNodes((nds) => [...nds, newTableau]);
    setNodeCount((prev) => ({ ...prev, tableau: prev.tableau + 1 }));
    setShowTableauModal(false);
    notifyParent();
  }, [nodeCount.tableau, setNodes, notifyParent]);

  // Sync all telemetry data into nodes (meter data, WattelEM telemetry, and deduced energy)
  // Combined into a single useEffect to avoid race conditions
  useEffect(() => {
    const meterMap = new Map(meterData.map(m => [Number(m.deviceId), m]));
    const currentNodes = nodesRef.current;
    const currentEdges = edgesRef.current;
    
    console.log('[TelemetrySync] Starting sync...', { 
      wattelemTelemetryKeys: Object.keys(wattelemTelemetry),
      meterDataLength: meterData.length,
      nodesCount: currentNodes.length,
      edgesCount: currentEdges.length 
    });
    
    // Find all tableaux with WattelEM and their connected zones (for deduced energy calculation)
    const tableauxWithWattelEM: Array<{ wattelemSn: string; zoneIds: string[] }> = [];
    
    currentNodes.forEach(n => {
      if (n.type === 'tableau' && n.data?.wattelemList && n.data.wattelemList.length > 0) {
        n.data.wattelemList.forEach((wem: any) => {
          // Find zones connected to this WattelEM via edges with sourceHandle "wem-{sn}"
          let connectedZones = currentEdges
            .filter(e => e.source === n.id && e.sourceHandle === `wem-${wem.sn}`)
            .map(e => e.target);
          
          // If no zones found with specific handle, try to find zones connected to the tableau
          if (connectedZones.length === 0) {
            connectedZones = currentEdges
              .filter(e => e.source === n.id && e.sourceHandle?.startsWith('wem-'))
              .map(e => e.target);
          }
          
          // Also check for zones connected directly (any edge from tableau to zone)
          if (connectedZones.length === 0) {
            const zoneNodes = currentNodes.filter(node => node.type === 'zone');
            const zoneIds = zoneNodes.map(z => z.id);
            connectedZones = currentEdges
              .filter(e => e.source === n.id && zoneIds.includes(e.target))
              .map(e => e.target);
          }
          
          if (connectedZones.length > 0) {
            tableauxWithWattelEM.push({
              wattelemSn: wem.sn,
              zoneIds: connectedZones,
            });
          }
        });
      }
    });
    
    // Build a map of equipment that should have deduced energy
    const deducedEnergyMap = new Map<string, number>();
    const deducedEquipmentIds = new Set<string>();
    
    if (Object.keys(wattelemTelemetry).length > 0) {
      tableauxWithWattelEM.forEach(({ wattelemSn, zoneIds }) => {
        const wattelemTel = wattelemTelemetry[wattelemSn];
        const wattelemEnergyKwh = Number(wattelemTel?.energy_kwh) || 0;
        
        if (wattelemEnergyKwh <= 0) return;
        
        const wattelemEnergyWh = wattelemEnergyKwh * 1000;
        
        zoneIds.forEach(zoneId => {
          const zoneEquipmentIds = currentEdges
            .filter(e => e.source === zoneId)
            .map(e => e.target);
          
          const zoneEquipment = currentNodes.filter(n => 
            n.type === 'point' && zoneEquipmentIds.includes(n.id)
          );
          
          if (zoneEquipment.length === 0) return;
          
          let totalMeasuredEnergyWh = 0;
          const unmeasuredEquipment: string[] = [];
          
          zoneEquipment.forEach(eq => {
            if (eq.data?.deviceId) {
              const meter = meterMap.get(eq.data.deviceId);
              if (meter) {
                totalMeasuredEnergyWh += (Number(meter.activeFEnergy) || 0) / 10_000; // raw → Wh
              }
            } else if (eq.data?.deviceSn) {
              const tel = wattelemTelemetry[eq.data.deviceSn];
              if (tel) {
                totalMeasuredEnergyWh += (Number(tel.energy_kwh) || 0) * 1000; // kWh → Wh
              }
            } else {
              unmeasuredEquipment.push(eq.id);
            }
          });
          
          if (unmeasuredEquipment.length > 0) {
            const deducedEnergyWh = Math.max(0, wattelemEnergyWh - totalMeasuredEnergyWh);
            const energyPerEquipmentWh = deducedEnergyWh / unmeasuredEquipment.length;
            
            console.log('[TelemetrySync] Deduced energy for', unmeasuredEquipment, ':', energyPerEquipmentWh, 'Wh');
            
            unmeasuredEquipment.forEach(eqId => {
              deducedEnergyMap.set(eqId, energyPerEquipmentWh);
              deducedEquipmentIds.add(eqId);
            });
          }
        });
      });
    }
    
    setNodes(nds => {
      let hasChanges = false;
      const updatedNodes = nds.map(n => {
        let newData = { ...n.data };
        let nodeChanged = false;
        
        // 1. Update meter data for point nodes with deviceId
        if (n.type === 'point' && n.data?.deviceId) {
          const meter = meterMap.get(n.data.deviceId);
          if (meter) {
            const meterPower = Number(meter.activeFPower) || 0;
            const meterEnergy = Number(meter.activeFEnergy) || 0;
            const meterVoltage = Number(meter.rmsVoltage) || 0;
            if (newData.meterPower !== meterPower || newData.meterEnergy !== meterEnergy || newData.meterVoltage !== meterVoltage) {
              newData.meterPower = meterPower;
              newData.meterEnergy = meterEnergy;
              newData.meterVoltage = meterVoltage;
              nodeChanged = true;
            }
          }
        }

        // 1b. Update meter data for point nodes linked via deviceSn (MQTT live)
        //     Pulls from wattelemTelemetry (keyed by SN) and converts to the
        //     "raw register" units the renderer expects (value / 10_000_000 = kW/kWh).
        if (n.type === 'point' && n.data?.deviceSn && !n.data?.deviceId) {
          const tel = wattelemTelemetry[n.data.deviceSn];
          if (tel) {
            const meterPower = (Number(tel.power_kw) || 0) * 10_000_000;
            const meterEnergy = (Number(tel.energy_kwh) || 0) * 10_000_000;
            const meterVoltage = Number(tel.voltage_v) || 0;
            if (newData.meterPower !== meterPower || newData.meterEnergy !== meterEnergy || newData.meterVoltage !== meterVoltage) {
              newData.meterPower = meterPower;
              newData.meterEnergy = meterEnergy;
              newData.meterVoltage = meterVoltage;
              nodeChanged = true;
            }
          }
        }
        
        // 2. Update WattelEM telemetry for tableau nodes
        if (n.type === 'tableau' && n.data?.wattelemList?.length && Object.keys(wattelemTelemetry).length > 0) {
          const updatedList = n.data.wattelemList.map((wem: any) => {
            const tel = wattelemTelemetry[wem.sn];
            if (!tel) return wem;
            return { 
              ...wem, 
              voltage: Number(tel.voltage_v) || 0, 
              current: Number(tel.current_a) || 0, 
              power: Number(tel.power_kw) || 0, 
              energy: Number(tel.energy_kwh) || 0 
            };
          });
          // Check if list changed
          if (JSON.stringify(updatedList) !== JSON.stringify(n.data.wattelemList)) {
            newData.wattelemList = updatedList;
            nodeChanged = true;
          }
        }
        
        // 3. Update deduced energy for unmeasured equipment
        if (deducedEquipmentIds.has(n.id)) {
          const deducedEnergy = deducedEnergyMap.get(n.id) || 0;
          if (newData.deducedEnergy !== deducedEnergy || !newData.isDeduced) {
            newData.deducedEnergy = deducedEnergy;
            newData.isDeduced = true;
            nodeChanged = true;
            console.log('[TelemetrySync] Updated', n.id, 'type:', n.type, 'with deducedEnergy:', deducedEnergy, 'isDeduced: true', 'fullData:', JSON.stringify(newData));
          }
        }
        
        if (nodeChanged) {
          hasChanges = true;
          return { ...n, data: newData };
        }
        return n;
      });
      
      if (hasChanges) {
        console.log('[TelemetrySync] Applied changes to nodes');
        // Notify parent of telemetry updates so ProfessionalView gets updated nodes
        if (onNodesEdgesChange) {
          setTimeout(() => {
            onNodesEdgesChange(updatedNodes, edgesRef.current);
          }, 100);
        }
      }
      return hasChanges ? updatedNodes : nds;
    });
  }, [meterData, wattelemTelemetry, setNodes, onNodesEdgesChange]);

  const onConnect = useCallback(
    (params: Connection) => {
      if (!editable) return;
      
      // Déterminer la couleur selon le type de connexion
      const sourceNode = nodes.find(n => n.id === params.source);
      const targetNode = nodes.find(n => n.id === params.target);
      
      let edgeColor = '#ec4899'; // Rose par défaut (zone → équipement)
      
      // Linky → Tableau : Gris foncé
      if (sourceNode?.type === 'linky' && targetNode?.type === 'tableau') {
        edgeColor = '#666666';
      }
      // Tableau/WattelEM → Zone : Cyan
      else if ((sourceNode?.type === 'tableau' || params.sourceHandle?.startsWith('wem-')) && targetNode?.type === 'zone') {
        edgeColor = '#06b6d4';
      }
      // Zone → Zone : Violet
      else if (sourceNode?.type === 'zone' && targetNode?.type === 'zone') {
        edgeColor = '#8b5cf6';
      }
      // Zone → Équipement/Point : Rose
      else if (sourceNode?.type === 'zone' && (targetNode?.type === 'point' || targetNode?.type === 'equipment')) {
        edgeColor = '#ec4899';
      }
      // Tableau → Point (direct equipment on tableau) : Rose
      else if (sourceNode?.type === 'tableau' && (targetNode?.type === 'point' || targetNode?.type === 'equipment')) {
        edgeColor = '#ec4899';
      }
      
      setEdges((eds) => addEdge({ 
        ...params, 
        type: 'smoothstep',
        animated: true,
        style: { 
          stroke: edgeColor, 
          strokeWidth: 2,
          transition: 'all 0.3s ease',
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: edgeColor,
        }
      }, eds));
      notifyParent();

      // When connecting zone → point, save equipment to DB
      const zoneNode = (sourceNode?.type === 'zone' || sourceNode?.type === 'tableau') ? sourceNode : (targetNode?.type === 'zone' || targetNode?.type === 'tableau') ? targetNode : null;
      const pointNode = sourceNode?.type === 'point' ? sourceNode : targetNode?.type === 'point' ? targetNode : null;

      if (zoneNode && pointNode && zoneNode.data?.dbId && !pointNode.data?.dbId) {
        // Point not yet in DB — create it
        architectureApi.addEquipment(zoneNode.data.dbId, {
          name: pointNode.data?.label || t('arch2_device_default'),
          icon: pointNode.data?.icon,
          type: pointNode.data?.type || pointNode.data?.pointType,
          power: Number(pointNode.data?.power) || null,
          positionX: pointNode.position.x,
          positionY: pointNode.position.y,
        }).then(res => {
          if (res.data?.id) {
            updateNodeData(pointNode.id, { dbId: res.data.id, parentZoneDbId: zoneNode.data.dbId });
          }
        }).catch(console.error);
      } else if (zoneNode && pointNode && zoneNode.data?.dbId && pointNode.data?.dbId) {
        // Point already in DB — move it to the new zone
        architectureApi.updateEquipment(pointNode.data.dbId, { zoneId: zoneNode.data.dbId }).then(() => {
          updateNodeData(pointNode.id, { parentZoneDbId: zoneNode.data.dbId });
        }).catch(console.error);
      }
    },
    [setEdges, editable, nodes, updateNodeData, notifyParent, t]
  );

  const onNodeClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      setSelectedNode(node);
      setSelectedEdge(null);
      onNodeSelect?.(node);
      
      // En mode consultation, clic sur tableau => callback externe
      if (!editable && node.type === 'tableau' && onTableauClick) {
        onTableauClick(node);
        return;
      }
      
      // En mode consultation, clic sur point => callback externe
      if (!editable && node.type === 'point' && onPointClick) {
        onPointClick(node);
        return;
      }
      
      // Open edit modal for zone, point, or tableau nodes
      if (node.type === 'zone') {
        setEditingZoneNode(node);
        setZoneSnError(null);
        setEditingPointNode(null);
        setEditingTableauNode(null);
      } else if (node.type === 'point') {
        setEditingPointNode(node);
        setPointSnInput('');
        setPointSnError(null);
        setEditingZoneNode(null);
        setEditingTableauNode(null);
      } else if (node.type === 'tableau') {
        setEditingTableauNode(node);
        setEditingZoneNode(null);
        setEditingPointNode(null);
      }
    },
    [onNodeSelect, editable, onTableauClick, onPointClick]
  );

  // Inject per-WattelEM click handler into tableau nodes so each WattelEM card
  // can navigate to its own equipment page (instead of opening all of them).
  const nodesForFlow = useMemo(
    () => nodes.map(n =>
      n.type === 'tableau'
        ? { ...n, data: { ...n.data, onWattelemClick, editable } }
        : n
    ),
    [nodes, onWattelemClick, editable]
  );

  const onEdgeClick = useCallback(
    (_: React.MouseEvent, edge: Edge) => {
      setSelectedEdge(edge);
      setSelectedNode(null);
    },
    []
  );

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
    setSelectedEdge(null);
    onNodeSelect?.(null);
    setEditingZoneNode(null);
    setEditingPointNode(null);
    setEditingTableauNode(null);
  }, [onNodeSelect]);

  // Fonction pour supprimer l'élément sélectionné
  const deleteSelectedElement = useCallback(() => {
    if (selectedNode) {
      const dbId = selectedNode.data?.dbId;
      // Supprimer de la base de données selon le type
      if (selectedNode.type === 'zone' && dbId) {
        architectureApi.deleteZone(dbId).catch(console.error);
      } else if (selectedNode.type === 'point' && dbId) {
        architectureApi.deleteEquipment(dbId).catch(console.error);
      }
      deleteNode(selectedNode.id);
      setSelectedNode(null);
    } else if (selectedEdge) {
      setEdges((eds) => eds.filter((e) => e.id !== selectedEdge.id));
      notifyParent();
      setSelectedEdge(null);
    }
  }, [selectedNode, selectedEdge, deleteNode, setEdges, notifyParent]);

  // Modal Component
  const SelectionModal = ({ 
    title, 
    items, 
    onSelect, 
    onClose 
  }: { 
    title: string; 
    items: { id: string; name: string; label?: string; icon: string; image?: string }[];
    onSelect: (item: any) => void; 
    onClose: () => void;
  }) => (
    <div className="absolute inset-0 bg-black/50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-gray-800">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-xl"
            aria-label={t('arch2_close')}
          >
            ✕
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {items.map((item) => (
            <button
              key={item.id}
              onClick={() => onSelect(item)}
              className="flex flex-col items-center gap-2 p-3 border-2 border-gray-200 rounded-lg hover:border-cyan-500 hover:bg-cyan-50 transition-all"
            >
              <span className="text-2xl">{item.icon}</span>
              <span className="text-sm font-medium text-gray-700">{item.label ?? item.name}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  // Modal spécifique pour les tableaux électriques avec détails
  const TableauSelectionModal = ({ 
    onSelect, 
    onClose 
  }: { 
    onSelect: (item: any) => void; 
    onClose: () => void;
  }) => {
    const [filter, setFilter] = useState('all');
    const [search, setSearch] = useState('');
    
    const q = search.toLowerCase();
    const filteredItems = TABLEAU_TYPES.filter(item => {
      const matchesSearch = item.name.toLowerCase().includes(q) ||
                           tableauDisplayName(item.name, t, tp).toLowerCase().includes(q) ||
                           item.marque.toLowerCase().includes(q) ||
                           tableauBrandLabel(item.marque, t).toLowerCase().includes(q);
      if (filter === 'all') return matchesSearch;
      if (filter === 'schneider') return matchesSearch && item.marque.includes('Schneider');
      if (filter === 'legrand') return matchesSearch && item.marque.includes('Legrand');
      if (filter === 'hager') return matchesSearch && item.marque.includes('Hager');
      if (filter === 'other') return matchesSearch && !item.marque.includes('Schneider') && !item.marque.includes('Legrand') && !item.marque.includes('Hager');
      return matchesSearch;
    });

    return (
      <div className="fixed inset-0 bg-black/70 z-[9999] flex items-center justify-center p-4">
        <div className="bg-gray-900 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden animate-fade-in-scale">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 flex justify-between items-center">
            <div>
              <h3 className="text-lg font-bold">{t('arch2_choose_panel')}</h3>
              <p className="text-xs text-blue-200">{t('arch2_choose_panel_sub')}</p>
            </div>
            <button
              onClick={onClose}
              aria-label={t('arch2_close')}
              className="bg-white/20 hover:bg-white/30 p-2 rounded-lg transition-colors"
            >
              ✕
            </button>
          </div>
          
          {/* Search & Filters */}
          <div className="p-4 bg-gray-800 border-b border-gray-700">
            <input
              type="text"
              placeholder={`🔍 ${t('arch2_search_item')}`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 mb-3 focus:outline-none focus:border-blue-500"
            />
            <div className="flex gap-2 flex-wrap">
              {[
                { id: 'all', label: t('arch2_all') },
                { id: 'schneider', label: 'Schneider' },
                { id: 'legrand', label: 'Legrand' },
                { id: 'hager', label: 'Hager' },
                { id: 'other', label: t('arch2_others') },
              ].map(f => (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                    filter === f.id 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Liste des tableaux */}
          <div className="p-4 overflow-y-auto max-h-[50vh] space-y-3">
            {filteredItems.map((item) => (
              <button
                key={item.id}
                onClick={() => onSelect(item)}
                className="w-full flex items-center gap-4 p-4 bg-gray-800 border-2 border-gray-700 rounded-xl hover:border-blue-500 hover:bg-gray-750 transition-all group"
              >
                {/* Icône tableau */}
                <div className={`w-14 h-14 rounded-lg flex items-center justify-center text-2xl
                  ${item.marque.includes('Schneider') ? 'bg-green-900/50 border border-green-600' : 
                    item.marque.includes('Legrand') ? 'bg-orange-900/50 border border-orange-600' :
                    item.marque.includes('Hager') ? 'bg-blue-900/50 border border-blue-600' :
                    'bg-gray-700 border border-gray-600'}`}>
                  {item.icon}
                </div>
                
                {/* Infos */}
                <div className="flex-1 text-left">
                  <div className="font-bold text-white group-hover:text-blue-400 transition-colors">
                    {tableauDisplayName(item.name, t, tp)}
                  </div>
                  <div className="text-sm text-gray-400">
                    {tp('sch_modules_count', item.modules)} • {tp('arch2_rows', item.rangees)}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full
                      ${item.marque.includes('Schneider') ? 'bg-green-900/50 text-green-400' : 
                        item.marque.includes('Legrand') ? 'bg-orange-900/50 text-orange-400' :
                        item.marque.includes('Hager') ? 'bg-blue-900/50 text-blue-400' :
                        'bg-gray-700 text-gray-400'}`}>
                      {tableauBrandLabel(item.marque, t)}
                    </span>
                    <span className="text-xs text-gray-500">{t('arch2_ref', { ref: item.ref })}</span>
                  </div>
                </div>
                
                {/* Bouton + */}
                <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center text-xl font-bold group-hover:bg-blue-500 transition-colors">
                  +
                </div>
              </button>
            ))}
          </div>
          
          {/* Footer */}
          <div className="p-4 bg-gray-800 border-t border-gray-700 flex justify-between items-center">
            <span className="text-sm text-gray-400">{tp('arch2_models_available', filteredItems.length)}</span>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition-colors"
            >
              {t('arch2_cancel')}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div ref={flowWrapperRef} className="w-full h-[600px] bg-gray-900 rounded-lg overflow-hidden relative">

      {/* Bouton Refresh en haut à droite */}
      <div className="absolute top-4 right-4 z-50 flex gap-2">
        <Btn size="sm" className="shadow-md" onClick={autoLayoutNodes} title={t('arch2_align_title')} icon={<IconAlign />}>{t('arch2_align')}</Btn>
        <Btn size="sm" className="shadow-md" onClick={() => window.location.reload()} title={t('arch2_refresh_title')} icon={<IconRefresh />}>{t('arch2_refresh')}</Btn>
      </div>

      {/* Bouton Supprimer flottant - apparaît quand un élément est sélectionné */}
      {(selectedNode || selectedEdge) && editable && (
        <Btn variant="danger" className="absolute top-4 left-1/2 -translate-x-1/2 z-50 shadow-xl border-red-200" onClick={deleteSelectedElement} title={t('arch2_delete_selected_title')} icon={<IconTrash />}>
          {selectedNode ? t('arch2_delete_block') : t('arch2_delete_link')}
        </Btn>
      )}

      {/* Toolbar */}
      {editable && (
        <div className="absolute top-4 left-4 z-10 flex gap-2 flex-wrap">
          {/* La couleur du point reprend celle de la légende (compteur, tableau, zone, point) */}
          <Btn size="sm" className="shadow-md" onClick={() => setShowCompteurModal(true)} icon={<Dot tone="amber" />}>{t('arch2_add_meter')}</Btn>
          <Btn size="sm" className="shadow-md" onClick={() => setShowTableauModal(true)} icon={<Dot tone="slate" />}>{t('arch2_add_panel')}</Btn>
          <Btn size="sm" className="shadow-md" onClick={() => setShowZoneModal(true)} icon={<Dot tone="cyan" />}>{t('arch2_add_zone')}</Btn>
          <Btn size="sm" className="shadow-md" onClick={() => setShowPointModal(true)} icon={<Dot tone="rose" />}>{t('arch2_add_point')}</Btn>
        </div>
      )}

      {/* Modals */}
      {showCompteurModal && (
        <SelectionModal
          title={t('arch2_choose_meter_type')}
          items={COMPTEUR_TYPES.map(c => ({ ...c, label: t(c.labelKey) }))}
          onSelect={addCompteur}
          onClose={() => setShowCompteurModal(false)}
        />
      )}
      {showTableauModal && (
        <TableauSelectionModal
          onSelect={addTableau}
          onClose={() => setShowTableauModal(false)}
        />
      )}
      {showZoneModal && (
        <SelectionModal
          title={t('arch2_choose_zone_type')}
          items={ZONE_TYPES.map(z => ({ ...z, label: t(z.labelKey) }))}
          onSelect={addZone}
          onClose={() => setShowZoneModal(false)}
        />
      )}
      {showPointModal && (
        <SelectionModal
          title={t('arch2_choose_device')}
          items={deviceModels.map(m => ({ ...m, id: m.code, label: deviceModelName(m, language), icon: m.icon || '⚙️', image: m.imageUrl || '' }))}
          onSelect={addPoint}
          onClose={() => setShowPointModal(false)}
        />
      )}

      {/* ====== ZONE DEVICE ASSIGNMENT MODAL ====== */}
      {editingZoneNode && (() => {
        // ── WattelEM disponibles pour cette zone ──
        // On ne propose que les WattelEM installés dans le(s) tableau(x)
        // disjoncteur(s) connecté(s) à cette zone (en remontant la chaîne
        // de parenté via les edges). Si aucun tableau connecté trouvé, on
        // retombe sur la liste complète (compatibilité).
        const parentMap = new Map<string, string>();
        edgesRef.current.forEach(e => parentMap.set(e.target, e.source));
        const allNodes = nodesRef.current;
        const allowedSns = new Set<string>();
        // Remonte depuis la zone jusqu'à trouver les tableaux ancêtres.
        let cursor: string | undefined = editingZoneNode.id;
        const visited = new Set<string>();
        while (cursor && !visited.has(cursor)) {
          visited.add(cursor);
          const node = allNodes.find(n => n.id === cursor);
          if (node?.type === 'tableau') {
            (node.data?.wattelemList || []).forEach((w: any) => {
              if (w?.sn) allowedSns.add(String(w.sn));
            });
          }
          cursor = parentMap.get(cursor);
        }
        const restrictToTableau = allowedSns.size > 0;

        return (
        <div className="absolute inset-0 bg-black/60 z-50 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-lg w-full mx-4 max-h-[80%] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-800">{t('arch2_zone_config')}</h3>
              <button onClick={() => setEditingZoneNode(null)} className="text-gray-500 hover:text-gray-700 text-xl" aria-label={t('arch2_close')}>✕</button>
            </div>

            {/* Zone Name */}
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-700 mb-0.5">{t('arch2_zone_name')}</label>
              <input
                type="text"
                value={editingZoneNode.data?.label || ''}
                onChange={(e) => {
                  updateNodeData(editingZoneNode.id, { label: e.target.value });
                  setEditingZoneNode({ ...editingZoneNode, data: { ...editingZoneNode.data, label: e.target.value } });
                }}
                onBlur={(e) => {
                  const dbId = editingZoneNode.data?.dbId;
                  if (dbId) architectureApi.updateZone(dbId, { name: e.target.value }).catch(console.error);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none"
              />
            </div>

            {/* WATTELEM Device Selector */}
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t('arch2_assign_wattelem')}
                <span className="text-xs text-gray-400 ml-1">{tp('arch2_devices_count', devices.filter(d => {
                  const sn = (d.deviceSn || '').toLowerCase();
                  const isWem = sn.includes('zce-em') || sn.includes('zce-we') || sn.includes('wattelem') || sn.includes('wattel_em');
                  return isWem && (!restrictToTableau || allowedSns.has(d.deviceSn));
                }).length)}</span>
              </label>
              {restrictToTableau && (
                <p className="text-[11px] text-cyan-600 mb-2">
                  {t('arch2_wattelem_from_panel')}
                </p>
              )}
              {zoneSnError && (
                <p className="mb-2 text-xs text-red-500 flex items-center gap-1">⚠️ {zoneSnError}</p>
              )}

              {devices.length === 0 ? (
                <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg text-sm text-orange-800">
                  {t('arch2_no_wattelem_found')}
                </div>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {/* Unassign option */}
                  <button
                    onClick={() => {
                      updateNodeData(editingZoneNode.id, { wattelemSn: undefined, power: 0, status: 'offline' });
                      setEditingZoneNode({ ...editingZoneNode, data: { ...editingZoneNode.data, wattelemSn: undefined, power: 0, status: 'offline' } });
                      const dbId = editingZoneNode.data?.dbId;
                      if (dbId) architectureApi.updateZone(dbId, { wattelemSn: '' }).catch(console.error);
                    }}
                    className={`w-full flex items-center gap-3 p-3 border-2 rounded-lg transition-all text-left ${
                      !editingZoneNode.data?.wattelemSn ? 'border-gray-500 bg-gray-50' : 'border-gray-200 hover:border-gray-400'
                    }`}
                  >
                    <span className="text-xl">🚫</span>
                    <div>
                      <div className="text-sm font-medium text-gray-700">{t('arch2_no_device')}</div>
                      <div className="text-xs text-gray-500">{t('arch2_do_not_assign')}</div>
                    </div>
                  </button>

                  {devices
                    .filter(d => {
                      // Ne montrer que les vrais WattelEM (pas WattelGo, WattelWall, prises, etc.)
                      const sn = (d.deviceSn || '').toLowerCase();
                      const isWem = sn.includes('zce-em') || sn.includes('zce-we') || sn.includes('wattelem') || sn.includes('wattel_em');
                      // ... et seulement ceux du tableau connecté si applicable
                      return isWem && (!restrictToTableau || allowedSns.has(d.deviceSn));
                    })
                    .map(d => {
                    const isAssigned = editingZoneNode.data?.wattelemSn === d.deviceSn;
                    const usedByOther = nodesRef.current.some(
                      n => n.type === 'zone' && n.id !== editingZoneNode.id && n.data?.wattelemSn === d.deviceSn
                    );
                    return (
                      <button
                        key={d.deviceSn}
                        disabled={usedByOther}
                        onClick={async () => {
                          // Exclusivité globale : refuse si déjà utilisé dans une
                          // autre installation (vérifié AVANT d'assigner).
                          try {
                            const excludeId = Number(sessionStorage.getItem('currentInstallationId')) || undefined;
                            const resp = await architectureApi.checkDeviceAvailable(d.deviceSn, excludeId);
                            if (!resp?.data?.available) {
                              setZoneSnError(t('arch2_device_in_use'));
                              return;
                            }
                          } catch {
                            setZoneSnError(t('arch2_device_check_failed'));
                            return;
                          }
                          setZoneSnError(null);
                          const powerNum = Number(d.powerKw) || 0;
                          updateNodeData(editingZoneNode.id, { wattelemSn: d.deviceSn, power: powerNum, status: d.online ? 'online' : 'offline' });
                          setEditingZoneNode({ ...editingZoneNode, data: { ...editingZoneNode.data, wattelemSn: d.deviceSn, power: powerNum, status: d.online ? 'online' : 'offline' } });
                          const dbId = editingZoneNode.data?.dbId;
                          if (dbId) architectureApi.updateZone(dbId, { wattelemSn: d.deviceSn }).catch(console.error);
                        }}
                        className={`w-full flex items-center gap-3 p-3 border-2 rounded-lg transition-all text-left ${
                          usedByOther ? 'border-gray-200 bg-gray-100 opacity-50 cursor-not-allowed'
                            : isAssigned ? 'border-cyan-500 bg-cyan-50'
                            : 'border-gray-200 hover:border-cyan-400 hover:bg-cyan-50'
                        }`}
                      >
                        <span className={`w-3 h-3 rounded-full ${d.online ? 'bg-green-500' : 'bg-red-500'}`}></span>
                        <div className="flex-1">
                          <div className="text-sm font-bold text-gray-800">{d.deviceSn}</div>
                          <div className="text-xs text-gray-500">
                            {d.online ? `🟢 ${t('arch2_online')}` : `🔴 ${t('arch2_offline')}`}
                            {d.powerKw !== null ? ` • ${d.powerKw} kW` : ''}
                            {d.voltageV !== null ? ` • ${d.voltageV}V` : ''}
                            {d.currentA !== null ? ` • ${d.currentA}A` : ''}
                          </div>
                          {usedByOther && <div className="text-xs text-orange-600 mt-1">{t('arch2_already_assigned_zone')}</div>}
                        </div>
                        {isAssigned && <span className="text-cyan-600 text-lg">✓</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Current assignment summary */}
            {editingZoneNode.data?.wattelemSn && (
              <div className="p-3 bg-cyan-50 border border-cyan-200 rounded-lg text-sm mb-4">
                <span className="font-medium text-cyan-800">{t('arch2_assigned')}</span>{' '}
                <span className="text-cyan-700">{editingZoneNode.data.wattelemSn}</span>
              </div>
            )}

            <div className="flex items-center justify-between mt-3 pt-2 border-t">
              <button
                onClick={() => {
                  const dbId = editingZoneNode.data?.dbId;
                  if (dbId) architectureApi.deleteZone(dbId).catch(console.error);
                  deleteNode(editingZoneNode.id);
                }}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm"
              >
                {t('arch2_delete_zone')}
              </button>
              <button
                onClick={() => setEditingZoneNode(null)}
                className="px-6 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 text-sm font-medium"
              >
                {t('arch2_close')}
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* ====== POINT/EQUIPMENT EDIT MODAL ====== */}
      {editingPointNode && (
        <div
          className="absolute inset-0 bg-black/60 z-50 flex items-center justify-center"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="bg-white rounded-xl shadow-2xl p-3 max-w-sm w-full mx-3 max-h-[80vh] overflow-y-auto"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm font-bold text-gray-800">{t('arch2_device_config')}</h3>
              <button onClick={() => setEditingPointNode(null)} className="text-gray-500 hover:text-gray-700 text-xl" aria-label={t('arch2_close')}>✕</button>
            </div>

            <div className="space-y-2">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-0.5">{t('arch2_name')}</label>
                <input
                  type="text"
                  value={editingPointNode.data?.label || ''}
                  onChange={(e) => {
                    updateNodeData(editingPointNode.id, { label: e.target.value });
                    setEditingPointNode({ ...editingPointNode, data: { ...editingPointNode.data, label: e.target.value } });
                  }}
                  onBlur={(e) => {
                    const dbId = editingPointNode.data?.dbId;
                    if (dbId) architectureApi.updateEquipment(dbId, { name: e.target.value }).catch(console.error);
                  }}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-0.5">{t('arch2_type')}</label>
                <div className="grid grid-cols-3 gap-1.5">
                  {deviceModels.map(pt => {
                    const currentType = editingPointNode.data?.type || editingPointNode.data?.pointType || '';
                    const isActive = currentType === pt.code;
                    const ptIcon = pt.icon || '⚙️';
                    return (
                      <button
                        key={pt.code}
                        onClick={() => {
                          updateNodeData(editingPointNode.id, { type: pt.code, icon: ptIcon, pointType: pt.code });
                          setEditingPointNode({ ...editingPointNode, data: { ...editingPointNode.data, type: pt.code, icon: ptIcon, pointType: pt.code } });
                          const dbId = editingPointNode.data?.dbId;
                          if (dbId) architectureApi.updateEquipment(dbId, { type: pt.code, icon: ptIcon }).catch(console.error);
                        }}
                        className={`flex flex-col items-center p-1 rounded-lg border transition-all ${
                          isActive ? 'border-pink-500 bg-pink-50 shadow-md' : 'border-gray-200 hover:border-gray-400 bg-white'
                        }`}
                      >
                        {pt.imageUrl ? (
                          <img src={pt.imageUrl} alt={deviceModelName(pt, language)} className="w-6 h-6 object-contain" />
                        ) : (
                          <span className="text-lg">{ptIcon}</span>
                        )}
                        <div className={`text-[10px] font-bold mt-1 ${isActive ? 'text-pink-600' : 'text-gray-600'}`}>{deviceModelName(pt, language)}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-0.5">{t('arch2_power_w')}</label>
                <input
                  type="number"
                  value={editingPointNode.data?.power ?? ''}
                  onChange={(e) => {
                    const v = e.target.value ? Number(e.target.value) : 0;
                    updateNodeData(editingPointNode.id, { power: v });
                    setEditingPointNode({ ...editingPointNode, data: { ...editingPointNode.data, power: v } });
                  }}
                  onBlur={(e) => {
                    const dbId = editingPointNode.data?.dbId;
                    if (dbId) architectureApi.updateEquipment(dbId, { power: e.target.value ? Number(e.target.value) : null }).catch(console.error);
                  }}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg outline-none"
                  placeholder={t('arch2_example_short', { v: 1200 })}
                />
              </div>

              {/* ====== DEVICE PICKER ====== */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('arch2_linked_device')}</label>
                {editingPointNode.data?.deviceSn ? (
                  <div className="flex items-center justify-between bg-green-50 border border-green-300 rounded-lg p-3">
                    <div>
                      <div className="text-sm font-bold text-green-800 flex items-center gap-1.5">
                        {displaySn(catalogDevices.find(d => d.deviceSn === editingPointNode.data?.deviceSn)?.deviceName) || displaySn(editingPointNode.data?.deviceName) || displaySn(editingPointNode.data.deviceSn)}
                        {editingPointNode.data?.wattelDeviceType === 'ocpp' && (
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-500 text-white">OCPP</span>
                        )}
                      </div>
                      <div className="text-xs text-green-600">
                        {t('sch_sn_label', { sn: displaySn(catalogDevices.find(d => d.deviceSn === editingPointNode.data?.deviceSn)?.deviceSn) || displaySn(editingPointNode.data.deviceSn) })}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        updateNodeData(editingPointNode.id, { deviceId: null, deviceName: null, deviceSn: null, wattelDeviceType: null });
                        setEditingPointNode({ ...editingPointNode, data: { ...editingPointNode.data, deviceId: null, deviceName: null, deviceSn: null, wattelDeviceType: null } });
                        const dbId = editingPointNode.data?.dbId;
                        if (dbId) architectureApi.updateEquipment(dbId, { deviceId: null, deviceSn: null, wattelDeviceType: null }).catch(console.error);
                      }}
                      className="px-3 py-1 bg-red-100 text-red-700 rounded text-xs hover:bg-red-200"
                    >
                      {t('arch2_remove')}
                    </button>
                  </div>
                ) : (() => {
                  // Association par NUMÉRO DE SÉRIE (au lieu de lister tous les appareils).
                  // Filtrage selon le TYPE choisi :
                  // - Wattel 7 / Wattel 22 → bornes OCPP (base OCPP)
                  // - Wattel Go → bornes OCPP + capteurs MQTT (les deux)
                  // - Wattel Wall / Autre → capteurs MQTT (WattelEM / catalogue)
                  // - Aucun type → on cherche dans les deux (sécurité)
                  const currentType = editingPointNode.data?.type || editingPointNode.data?.pointType || '';
                  const isOcppType = ['wattel7', 'wattel22'].includes(currentType);
                  const isWattelGo = currentType === 'wattelgo';
                  const showOcpp = isOcppType || isWattelGo || currentType === '';
                  const showMqtt = !isOcppType; // wattelgo, wattelwall, autre, ou aucun type → MQTT

                  const handleAddPointSn = async () => {
                    const sn = pointSnInput.trim();
                    if (!sn) return;
                    setPointSnError(null);

                    // Déjà associé à un autre point (dans ce même schéma) ?
                    const usedElsewhere = nodes.some(
                      n => n.type === 'point' && n.id !== editingPointNode.id &&
                        String(n.data?.deviceSn || '').toLowerCase() === sn.toLowerCase()
                    );
                    if (usedElsewhere) {
                      setPointSnError(t('arch2_sn_used_other_point'));
                      return;
                    }

                    // Résout le device (OCPP d'abord, puis MQTT) selon le type
                    const ocpp = showOcpp
                      ? ocppBornes.find(b => String(b.deviceSn || '').toLowerCase() === sn.toLowerCase())
                      : undefined;
                    const mqtt = !ocpp && showMqtt
                      ? catalogDevices.find(d => String(d.deviceSn || '').toLowerCase() === sn.toLowerCase())
                      : undefined;

                    if (!ocpp && !mqtt) {
                      setPointSnError(
                        isOcppType
                          ? t('arch2_no_ocpp_found')
                          : t('arch2_no_device_ocpp_mqtt')
                      );
                      return;
                    }

                    // Exclusivité globale : refuse si déjà utilisé dans une autre
                    // installation (vérifié côté backend AVANT d'associer).
                    const resolvedSn = ocpp ? ocpp.deviceSn : mqtt!.deviceSn;
                    setPointSnChecking(true);
                    try {
                      const excludeId = Number(sessionStorage.getItem('currentInstallationId')) || undefined;
                      const resp = await architectureApi.checkDeviceAvailable(resolvedSn, excludeId);
                      if (!resp?.data?.available) {
                        setPointSnError(t('arch2_device_in_use'));
                        return;
                      }
                    } catch {
                      setPointSnError(t('arch2_device_check_failed'));
                      return;
                    } finally {
                      setPointSnChecking(false);
                    }

                    if (ocpp) {
                      updateNodeData(editingPointNode.id, {
                        deviceId: ocpp.deviceId, deviceName: ocpp.deviceName, deviceSn: ocpp.deviceSn,
                        wattelDeviceType: 'ocpp', meterPower: 0, meterEnergy: 0, meterVoltage: 0,
                      });
                      setEditingPointNode(prev => prev ? { ...prev, data: { ...prev.data, deviceId: ocpp.deviceId, deviceName: ocpp.deviceName, deviceSn: ocpp.deviceSn, wattelDeviceType: 'ocpp', meterPower: 0, meterEnergy: 0, meterVoltage: 0 } } : prev);
                      const dbId = editingPointNode.data?.dbId;
                      if (dbId) architectureApi.updateEquipment(dbId, { deviceId: ocpp.deviceId, deviceSn: ocpp.deviceSn, wattelDeviceType: 'ocpp' }).catch(err => console.error('[DevicePicker][OCPP] save failed', err));
                      setPointSnInput(''); setPointSnError(null);
                      return;
                    }

                    // MQTT
                    updateNodeData(editingPointNode.id, {
                      deviceId: null, deviceName: mqtt!.deviceName, deviceSn: mqtt!.deviceSn,
                      wattelDeviceType: null, meterPower: 0, meterEnergy: 0, meterVoltage: 0,
                    });
                    setEditingPointNode(prev => prev ? { ...prev, data: { ...prev.data, deviceId: null, deviceName: mqtt!.deviceName, deviceSn: mqtt!.deviceSn, wattelDeviceType: null, meterPower: 0, meterEnergy: 0, meterVoltage: 0 } } : prev);
                    const dbId = editingPointNode.data?.dbId;
                    if (dbId) architectureApi.updateEquipment(dbId, { deviceId: null, deviceSn: mqtt!.deviceSn, wattelDeviceType: null }).catch(err => console.error('[DevicePicker] save failed', err));
                    setPointSnInput(''); setPointSnError(null);
                  };

                  return (
                    <div>
                      <p className="text-xs text-gray-500 mb-2">
                        {isOcppType ? t('arch2_enter_sn_ocpp') : showOcpp ? t('arch2_enter_sn_both') : t('arch2_enter_sn_mqtt')}
                      </p>
                      <div className="flex gap-2 items-start">
                        <div className="flex-1">
                          <input
                            type="text"
                            value={pointSnInput}
                            onChange={(e) => { setPointSnInput(e.target.value); if (pointSnError) setPointSnError(null); }}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddPointSn(); } }}
                            onMouseDown={(e) => e.stopPropagation()}
                            placeholder={t('arch2_example', { v: 'WG-00123456' })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-pink-500 focus:border-pink-500 outline-none"
                          />
                          {pointSnError && (
                            <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">⚠️ {pointSnError}</p>
                          )}
                        </div>
                        <button
                          type="button"
                          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                          onClick={handleAddPointSn}
                          disabled={!pointSnInput.trim() || pointSnChecking}
                          className="px-4 py-2 bg-pink-600 hover:bg-pink-700 text-white text-sm font-semibold rounded-lg shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-1.5"
                        >
                          {pointSnChecking ? (
                            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                          )}
                          {t('arch2_link')}
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            <div className="flex items-center justify-between mt-6 pt-4 border-t">
              <button
                onClick={() => {
                  const dbId = editingPointNode.data?.dbId;
                  if (dbId) architectureApi.deleteEquipment(dbId).catch(console.error);
                  deleteNode(editingPointNode.id);
                }}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm"
              >
                {t('arch2_delete_icon')}
              </button>
              <button
                onClick={() => setEditingPointNode(null)}
                className="px-6 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 text-sm font-medium"
              >
                {t('arch2_close')}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* ====== TABLEAU ÉLECTRIQUE MODAL - Vue Réaliste ====== */}
      <TableauElectriqueModal
        editingTableauNode={editingTableauNode}
        setEditingTableauNode={setEditingTableauNode}
        devices={devices}
        addWattelemToPanel={addWattelemToPanel}
        removeWattelemFromPanel={removeWattelemFromPanel}
      />

      <ReactFlow
        nodes={nodesForFlow}
        edges={edges}
        onNodesChange={editable ? onNodesChange : undefined}
        onEdgesChange={editable ? onEdgesChange : undefined}
        onConnect={onConnect}
        onNodeClick={onNodeClick}
        onEdgeClick={onEdgeClick}
        onNodeDragStop={onNodeDragStop}
        onPaneClick={onPaneClick}
        nodeTypes={nodeTypes}
        nodesDraggable={editable}
        nodesConnectable={editable}
        elementsSelectable={true}
        selectNodesOnDrag={false}
        panOnDrag={[1, 2]}
        panOnScroll={false}
        zoomOnScroll={true}
        zoomOnPinch={true}
        minZoom={0.2}
        maxZoom={2}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        defaultEdgeOptions={{
          type: 'default', // default = straight line
          animated: false,
          style: { 
            stroke: '#ec4899', 
            strokeWidth: 4, // Encore plus épais
            zIndex: 1000, // Au-dessus de tout
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: '#ec4899',
            width: 25,
            height: 25,
          },
        }}
      >
        <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#374151" />
        <Controls className="bg-white rounded-lg" />
      </ReactFlow>
    </div>
  );
}

// Main export component with ReactFlowProvider wrapper
export function ElectricalSchemaFlow(props: ElectricalSchemaFlowProps) {
  return (
    <ReactFlowProvider>
      <ElectricalSchemaFlowInternal {...props} />
    </ReactFlowProvider>
  );
}

export default ElectricalSchemaFlow;


